import React, { useState, useEffect, useRef } from 'react';
import { 
  Layers, Shield, ShieldCheck, Network, Cpu, Workflow, ArrowRight, Database, 
  Lock, Zap, Activity, Search, Globe, Radio, CheckCircle2, AlertTriangle, 
  TrendingUp, Target, Truck, Briefcase, Info, X, Filter, Home, Cloud, Anchor,
  PlayCircle, Server, ArrowUpRight, Maximize2, Box, MapPin, Factory, Command, MoveLeft,
  Flame, Star, ShoppingBag, Book, Leaf, PenTool, Settings, Navigation, Terminal, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, Eye, Scale, BrainCircuit, Rocket
} from 'lucide-react';

// --- NEURAL MESH ---
const NeuralMesh = ({ opacity = 0.08, color = "99, 102, 241" }) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const resize = () => {
      if (canvas.parentElement) {
        canvas.width = canvas.parentElement.offsetWidth;
        canvas.height = canvas.parentElement.offsetHeight;
      }
    };
    resize();
    window.addEventListener('resize', resize);
    let animationFrame;
    const nodes = Array.from({ length: 40 }, () => ({
      x: Math.random() * (canvas.width || 800),
      y: Math.random() * (canvas.height || 600),
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4
    }));
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = `rgba(${color}, ${opacity})`;
      ctx.strokeStyle = `rgba(${color}, ${opacity * 0.5})`;
      nodes.forEach(node => {
        node.x += node.vx; node.y += node.vy;
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
        ctx.beginPath(); ctx.arc(node.x, node.y, 1.2, 0, Math.PI * 2); ctx.fill();
      });
      nodes.forEach((n1, i) => {
        nodes.slice(i + 1).forEach(n2 => {
          const dx = n1.x - n2.x; const dy = n1.y - n2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) { ctx.beginPath(); ctx.moveTo(n1.x, n1.y); ctx.lineTo(n2.x, n2.y); ctx.stroke(); }
        });
      });
      animationFrame = requestAnimationFrame(animate);
    };
    animate();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(animationFrame); };
  }, [opacity, color]);
  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />;
};

// --- DATA: FULLY VERIFIED PORTFOLIO FIRMS & SCORES (33 ASSETS) ---
const PortfolioData = [
  // L1: ARTERY (RESILIENCE)
  { 
    company: "HrdWyr", domain: "hrdwyr.com", sector: "Semis", layer: "Artery", ms: 4.9, rs: 1.1, quadrant: "Titans", color: "emerald", moat: "Proprietary physical silicon (AI-native SoC architecture) at the edge.", verdict: "Anchor as the absolute sovereign compute substrate of the portfolio.",
    moatBreakdown: [{ factor: "P-Foundation", score: 5, rationale: "Full-stack fabless semiconductor asset designing physical silicon." }, { factor: "H-Accretion", score: 4.8, rationale: "Custom TEA Framework embeds ML directly into hardware architecture." }, { factor: "W-Sovereignty", score: 4.9, rationale: "Inextricably locked into physical assembly paths of downstream OEMs." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 0.5, rationale: "Immune to software commoditization; LLMs cannot replicate physical silicon." }, { factor: "L-Scaling", score: 2.0, rationale: "Fabless design scales exponentially via foundry execution." }, { factor: "O-Dependency", score: 1.0, rationale: "Operates locally on-device without persistent cloud pipelines." }],
    currentMultiple: "13x", targetMultiple: "25x+", alphaDelta: "Secure", intervention: "Weaponize as foundational silicon architecture to eliminate offshore fragility."
  },
  { 
    company: "BluJ Aero", domain: "blujaero.com", sector: "Defense / Log", layer: "Artery", ms: 4.2, rs: 1.4, quadrant: "Titans", color: "emerald", moat: "Scalable VTOL architecture & hybrid propulsion.", verdict: "High asset control; anchor for logistics autonomy.",
    moatBreakdown: [{ factor: "P-Foundation", score: 5, rationale: "Hard aerospace IP. VTOL hardware design and propulsion." }, { factor: "H-Accretion", score: 3, rationale: "Flight control heuristics." }, { factor: "W-Sovereignty", score: 4.5, rationale: "Critical node for sovereign supply chain." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 1, rationale: "Physical manufacturing defies code commoditization." }, { factor: "L-Scaling", score: 2, rationale: "Hardware scaling requires capex." }, { factor: "O-Dependency", score: 1.5, rationale: "Self-contained asset." }],
    currentMultiple: "12x", targetMultiple: "12x", alphaDelta: "Secure", intervention: "Protect IP. Expand defense contracts."
  },
  { 
    company: "SmartShip", domain: "smartshiphub.com", sector: "Logistics", layer: "Artery", ms: 3.9, rs: 3.4, quadrant: "Spines", color: "emerald", moat: "Single source of truth for maritime telemetry.", verdict: "Fuse with physical assets for Global Freight Reset.",
    moatBreakdown: [{ factor: "P-Foundation", score: 4, rationale: "Aggregates telemetry from vessel sensors." }, { factor: "H-Accretion", score: 3, rationale: "Predictive routing heuristics." }, { factor: "W-Sovereignty", score: 4.5, rationale: "Terminal OS for fleet operators." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 4, rationale: "Routing logic commoditized by broad AI." }, { factor: "L-Scaling", score: 2, rationale: "Vessel hardware integration friction." }, { factor: "O-Dependency", score: 1.5, rationale: "Relies on sensor integrity." }],
    currentMultiple: "6x", targetMultiple: "10x", alphaDelta: "+4x", intervention: "Evolve to autonomous routing authority.", pivotPath: "Shift from Telemetry UI to Autonomous Authority."
  },
  { 
    company: "Numocity", domain: "numocity.com", sector: "Energy & Utils", layer: "Artery", ms: 4.5, rs: 1.5, quadrant: "Titans", color: "amber", moat: "EV Grid-tied hardware communication protocol keys.", verdict: "Scale as the absolute sovereign EV infrastructure layer.",
    moatBreakdown: [{ factor: "P-Foundation", score: 5, rationale: "Controls the physical energy dispatch endpoints." }, { factor: "H-Accretion", score: 4, rationale: "Dynamic load-balancing energy algorithms." }, { factor: "W-Sovereignty", score: 4.5, rationale: "Locked into grid operator workflows." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 1, rationale: "LLMs cannot dispatch physical grid electricity." }, { factor: "L-Scaling", score: 2, rationale: "Hardware deployment velocity limits." }, { factor: "O-Dependency", score: 1.5, rationale: "High regulatory lock-in." }],
    currentMultiple: "14x", targetMultiple: "16x", alphaDelta: "Secure", intervention: "Consolidate regional transport electrification mesh."
  },
  { 
    company: "Phyx44", domain: "phyx44.com", sector: "Energy & Utils", layer: "Artery", ms: 4.7, rs: 1.1, quadrant: "Titans", color: "amber", moat: "Clean energy substrate and hydrogen generation physics.", verdict: "Anchor as a critical defense spine asset.",
    moatBreakdown: [{ factor: "P-Foundation", score: 5, rationale: "Hard IP in thermodynamics and energy generation." }, { factor: "H-Accretion", score: 4.5, rationale: "Proprietary molecular synthesis models." }, { factor: "W-Sovereignty", score: 4.5, rationale: "Base-layer energy reliance." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 1, rationale: "Pure hard science; immune to logic abstraction." }, { factor: "L-Scaling", score: 1.5, rationale: "Requires specialized manufacturing." }, { factor: "O-Dependency", score: 1, rationale: "Fully sovereign." }],
    currentMultiple: "18x", targetMultiple: "20x+", alphaDelta: "Secure", intervention: "Protect deep-tech patents. Map to sovereign defense."
  },
  { 
    company: "Simyog", domain: "simyog.com", sector: "Mfg", layer: "Artery", ms: 4.1, rs: 3.1, quadrant: "Spines", color: "orange", moat: "Virtual EMI/EMC physics laboratory.", verdict: "Pivot to a physics-based sovereign reasoning spine.",
    moatBreakdown: [{ factor: "P-Foundation", score: 3, rationale: "Simulates hardware environments." }, { factor: "H-Accretion", score: 5, rationale: "Physics-based reasoning heuristics." }, { factor: "W-Sovereignty", score: 4.5, rationale: "Embedded in automotive design paths." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 4, rationale: "Advanced LLMs bridging complex math." }, { factor: "L-Scaling", score: 3, rationale: "Enterprise sales cycle is long." }, { factor: "O-Dependency", score: 1, rationale: "Standalone engine." }],
    currentMultiple: "7x", targetMultiple: "12x", alphaDelta: "+5x", intervention: "Productize physics datasets.", pivotPath: "Transition to Sovereign Reasoning Spine."
  },

  // L2: PERIMETER (INFRASTRUCTURE)
  { 
    company: "Lavelle Networks", domain: "lavellenetworks.com", sector: "Telecom", layer: "Perimeter", ms: 4.1, rs: 1.9, quadrant: "Titans", color: "indigo", moat: "Proprietary SD-WAN traffic control plane.", verdict: "Architect as the high-asset intelligence backbone.",
    moatBreakdown: [{ factor: "P-Foundation", score: 3, rationale: "Physical network routing plane." }, { factor: "H-Accretion", score: 4, rationale: "Traffic management latency heuristics." }, { factor: "W-Sovereignty", score: 5, rationale: "Enterprise artery." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 2, rationale: "Routing shielded from LLM commoditization." }, { factor: "L-Scaling", score: 1, rationale: "Zero-touch provisioning." }, { factor: "O-Dependency", score: 3, rationale: "Underlying ISP reliance." }],
    currentMultiple: "10x", targetMultiple: "12x", alphaDelta: "+2x", intervention: "Deepen integration with Sovereign AI data flows."
  },
  { 
    company: "Okulo Aerospace", domain: "okuloaerospace.com", sector: "Space", layer: "Perimeter", ms: 3.8, rs: 1.7, quadrant: "Titans", color: "emerald", moat: "Solar-electric endurance for persistent monitoring.", verdict: "Sovereign asset foundation for energy & defense.",
    moatBreakdown: [{ factor: "P-Foundation", score: 5, rationale: "Solar-endurance hardware platform." }, { factor: "H-Accretion", score: 3, rationale: "Aerial telemetry heuristics." }, { factor: "W-Sovereignty", score: 3.5, rationale: "Legacy satellite replacement." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 1, rationale: "Origin sensor; LLMs consume its data." }, { factor: "L-Scaling", score: 3, rationale: "Hardware production bottlenecks." }, { factor: "O-Dependency", score: 1.5, rationale: "Operationally sovereign." }],
    currentMultiple: "8x", targetMultiple: "10x", alphaDelta: "+2x", intervention: "Exclusive data feed for Defense AI."
  },
  { 
    company: "Accelequant", domain: "accelequant.com", sector: "Compute", layer: "Perimeter", ms: 4.0, rs: 1.2, quadrant: "Titans", color: "cyan", moat: "Hybrid quantum platform moat.", verdict: "Technical moat invulnerable to classical LLMs.",
    moatBreakdown: [{ factor: "P-Foundation", score: 4, rationale: "Hybrid quantum-classical algorithmic foundation." }, { factor: "H-Accretion", score: 5, rationale: "Deep quantum computing heuristics." }, { factor: "W-Sovereignty", score: 3.5, rationale: "Specialized computational workflows." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 1, rationale: "Quantum advantage isolated from LLMs." }, { factor: "L-Scaling", score: 1, rationale: "Software-based cloud scaling." }, { factor: "O-Dependency", score: 2, rationale: "Quantum hardware providers." }],
    currentMultiple: "15x", targetMultiple: "15x+", alphaDelta: "Secure", intervention: "Accelerate strategic partnerships."
  },
  { 
    company: "Nimesa", domain: "nimesa.com", sector: "Cloud", layer: "Perimeter", ms: 2.9, rs: 1.8, quadrant: "Harvest", color: "indigo", moat: "Data backup and recovery workflow.", verdict: "Stabilize as a reliable perimeter utility.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Cloud-native backup." }, { factor: "H-Accretion", score: 2, rationale: "Standard backup scheduling logic." }, { factor: "W-Sovereignty", score: 4.5, rationale: "High switching cost." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 2, rationale: "AI does not replace backups." }, { factor: "L-Scaling", score: 2, rationale: "Easy cloud deployment." }, { factor: "O-Dependency", score: 1, rationale: "Standalone." }],
    currentMultiple: "3x", targetMultiple: "4x", alphaDelta: "+1x", intervention: "Prepare for trade exit.", matchmaking: [{ buyer: "Dell / EMC", gap: "9.2", logic: "Infra Obsolescence", play: "Cash-yield asset." }]
  },
  { 
    company: "Spanugo", domain: "ibm.com", sector: "Cloud", layer: "Perimeter", ms: 4.2, rs: 1.6, quadrant: "Titans", color: "blue", moat: "Multi-cloud configuration auditing scripts.", verdict: "Retain data logic to guide compliance baselines.",
    moatBreakdown: [{ factor: "P-Foundation", score: 3, rationale: "Sits across multi-cloud endpoints." }, { factor: "H-Accretion", score: 4.5, rationale: "Deep security posture control mappings." }, { factor: "W-Sovereignty", score: 5, rationale: "Critical enterprise compliance node." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 2, rationale: "Highly specific compliance logic." }, { factor: "L-Scaling", score: 1, rationale: "Automated scaling." }, { factor: "O-Dependency", score: 2, rationale: "Cloud vendor APIs." }],
    currentMultiple: "Realized", targetMultiple: "Exit", alphaDelta: "Closed", intervention: "Realized Alpha via IBM exit."
  },
  { 
    company: "Ambee", domain: "getambee.com", sector: "Environmental", layer: "Perimeter", ms: 4.0, rs: 2.5, quadrant: "Spines", color: "emerald", moat: "Hyperlocal climate and air quality telemetry.", verdict: "Position as the exclusive real-world context feed.",
    moatBreakdown: [{ factor: "P-Foundation", score: 4, rationale: "Aggregates proprietary spatial sensor data." }, { factor: "H-Accretion", score: 4, rationale: "Hyperlocal interpolation algorithms." }, { factor: "W-Sovereignty", score: 4, rationale: "API locked into ag-tech and health systems." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 3, rationale: "Public climate datasets are improving." }, { factor: "L-Scaling", score: 1.5, rationale: "High margin API delivery." }, { factor: "O-Dependency", score: 3, rationale: "Relies on ground sensor networks." }],
    currentMultiple: "6x", targetMultiple: "10x", alphaDelta: "+4x", intervention: "Anchor into sovereign insurance risk-underwriting brains.", pivotPath: "Transition from API feed to Macro Risk Predictor."
  },
  { 
    company: "InfiSecure", domain: "infisecure.com", sector: "Cyber", layer: "Perimeter", ms: 3.8, rs: 3.0, quadrant: "Spines", color: "purple", moat: "Fingerprinted non-human web traffic vectors.", verdict: "Integrate into trust network architectures.",
    moatBreakdown: [{ factor: "P-Foundation", score: 3, rationale: "Reverse proxy screening." }, { factor: "H-Accretion", score: 4.5, rationale: "Deep bot-mitigation heuristics." }, { factor: "W-Sovereignty", score: 4, rationale: "Protects critical revenue pathways." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 4, rationale: "AI-generated bots vs AI detection arms race." }, { factor: "L-Scaling", score: 2, rationale: "Scalable but computationally heavy." }, { factor: "O-Dependency", score: 2.5, rationale: "Traffic ingestion reliance." }],
    currentMultiple: "5x", targetMultiple: "8x", alphaDelta: "+3x", intervention: "Insulate substrates from offensive model-scraping.", pivotPath: "Pivot to AI-Agent Firewalls."
  },
  { 
    company: "Saigeware", domain: "saigeware.com", sector: "Cyber", layer: "Perimeter", ms: 3.9, rs: 2.8, quadrant: "Spines", color: "indigo", moat: "Signatureless endpoint data behavioral telemetry.", verdict: "Vectorize edge heuristics to shield internal loops.",
    moatBreakdown: [{ factor: "P-Foundation", score: 3, rationale: "Endpoint agent deployment." }, { factor: "H-Accretion", score: 4.5, rationale: "Predictive behavioral maps." }, { factor: "W-Sovereignty", score: 4, rationale: "Embedded in security operations." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 3, rationale: "Crowdstrike/Palo Alto native features." }, { factor: "L-Scaling", score: 2.5, rationale: "Endpoint integration friction." }, { factor: "O-Dependency", score: 2, rationale: "OS-level permissions." }],
    currentMultiple: "6x", targetMultiple: "9x", alphaDelta: "+3x", intervention: "Shield private sovereign network segments.", pivotPath: "Transition to Autonomous Threat Neutralizer."
  },
  { 
    company: "Gyrophint", domain: "gyrophint.com", sector: "Compute", layer: "Perimeter", ms: 2.8, rs: 2.2, quadrant: "Harvest", color: "blue", moat: "Low-level execution tracing metrics.", verdict: "Isolate as a low-overhead profiling utility.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Sits on top of application memory." }, { factor: "H-Accretion", score: 3, rationale: "Runtime memory bottleneck heuristics." }, { factor: "W-Sovereignty", score: 3.5, rationale: "High utility for engineering teams." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 3, rationale: "Native cloud profiling tools are advancing." }, { factor: "L-Scaling", score: 1.5, rationale: "Lightweight deployment." }, { factor: "O-Dependency", score: 2, rationale: "Framework specific." }],
    currentMultiple: "3x", targetMultiple: "4x", alphaDelta: "+1x", intervention: "Support deep modernization cycles.", matchmaking: [{ buyer: "Datadog / New Relic", gap: "9.1", logic: "Tracing Depth", play: "Feature absorption." }]
  },

  // L3: REASONING (COGNITIVE)
  { 
    company: "Litemed", domain: "litemed.co.in", sector: "HCLS", layer: "Reasoning", ms: 3.2, rs: 1.8, quadrant: "Harvest", color: "cyan", moat: "Clinical instrumentation hardware architecture.", verdict: "Maintain margin optimization trajectory.",
    moatBreakdown: [{ factor: "P-Foundation", score: 4, rationale: "Medical-grade calibration and hardware." }, { factor: "H-Accretion", score: 2, rationale: "Standard clinical processing logic." }, { factor: "W-Sovereignty", score: 3.5, rationale: "High regulatory replacement friction." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 2, rationale: "Hardware bounds digital disruption." }, { factor: "L-Scaling", score: 2.5, rationale: "Hardware manufacturing limits." }, { factor: "O-Dependency", score: 1, rationale: "Independent utility." }],
    currentMultiple: "4x", targetMultiple: "5x", alphaDelta: "+1x", intervention: "Capital-efficient yield asset.", matchmaking: [{ buyer: "Philips / GE Healthcare", gap: "8.5", logic: "Portfolio Rollup", play: "Utility Hand-off." }]
  },
  { 
    company: "ManageArtworks", domain: "manageartworks.com", sector: "HCLS", layer: "Reasoning", ms: 4.4, rs: 3.1, quadrant: "Spines", color: "purple", moat: "Workflow sovereignty in regulated packaging.", verdict: "Pivot to the Sovereign Compliance Spine for Bio-Digital.",
    moatBreakdown: [{ factor: "P-Foundation", score: 4, rationale: "Owns the terminal regulatory interface." }, { factor: "H-Accretion", score: 4, rationale: "Deep compliance logic encoded." }, { factor: "W-Sovereignty", score: 5, rationale: "Absolute workflow sovereignty." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 3, rationale: "Vision AI improving at QA." }, { factor: "L-Scaling", score: 3, rationale: "Bespoke integration required." }, { factor: "O-Dependency", score: 3.5, rationale: "Relies on enterprise ERPs." }],
    currentMultiple: "6x", targetMultiple: "14x", alphaDelta: "+8x", intervention: "Vectorize regulatory corpus.", pivotPath: "Shift from Seat-License to Outcome-Based Yield. Embed as Brain."
  },
  { 
    company: "K-Cat", domain: "kcat.co.in", sector: "HCLS", layer: "Reasoning", ms: 4.9, rs: 1.0, quadrant: "Titans", color: "cyan", moat: "Enzyme engineering physics simulation.", verdict: "Scale as primary architecture for synthetic biology.",
    moatBreakdown: [{ factor: "P-Foundation", score: 4.5, rationale: "Quantum-level chemical model simulations." }, { factor: "H-Accretion", score: 5, rationale: "Proprietary ML heuristics for enzymes." }, { factor: "W-Sovereignty", score: 5, rationale: "Critical path for drug discovery." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 1, rationale: "Deep biology physics isolates from generic LLMs." }, { factor: "L-Scaling", score: 1, rationale: "Pure computational scaling." }, { factor: "O-Dependency", score: 1, rationale: "Self-contained logic." }],
    currentMultiple: "16x", targetMultiple: "20x", alphaDelta: "Secure", intervention: "Deep structural alpha asset. Accelerate lab partnerships."
  },
  { 
    company: "Ahammune", domain: "ahammune.com", sector: "HCLS", layer: "Reasoning", ms: 5.0, rs: 0.8, quadrant: "Titans", color: "emerald", moat: "Cellular signaling pathway blocker models.", verdict: "Anchor engine for bio-digital therapeutics synthesis.",
    moatBreakdown: [{ factor: "P-Foundation", score: 5, rationale: "Hard IP on dermatological compounds." }, { factor: "H-Accretion", score: 5, rationale: "Deep clinical trial heuristic loops." }, { factor: "W-Sovereignty", score: 5, rationale: "FDA-gated sovereignty." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 0.5, rationale: "Immune to logical model commoditization." }, { factor: "L-Scaling", score: 1, rationale: "IP licensing scales infinitely." }, { factor: "O-Dependency", score: 1, rationale: "Fully sovereign." }],
    currentMultiple: "20x", targetMultiple: "25x", alphaDelta: "Secure", intervention: "Defend IP. Prepare for massive Big Pharma premium exit."
  },
  { 
    company: "LatentForce", domain: "latentforce.ai", sector: "IT Services", layer: "Reasoning", ms: 3.7, rs: 3.5, quadrant: "Spines", color: "indigo", moat: "Automated root-cause analysis logic.", verdict: "Context-layer for automated IT operations.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Analyzes existing telemetry." }, { factor: "H-Accretion", score: 4, rationale: "AIOps resolution logic." }, { factor: "W-Sovereignty", score: 5, rationale: "Embedded in incident response." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 5, rationale: "AIOps is native cloud target." }, { factor: "L-Scaling", score: 2, rationale: "Complex integration maps." }, { factor: "O-Dependency", score: 2, rationale: "Standard logging frameworks." }],
    currentMultiple: "5x", targetMultiple: "9x", alphaDelta: "+4x", intervention: "Embed proprietary reasoning to prevent commoditization.", pivotPath: "Transition to Autonomous IT Concierge."
  },
  { 
    company: "Mihup", domain: "mihup.com", sector: "IT Services", layer: "Reasoning", ms: 2.8, rs: 2.2, quadrant: "Harvest", color: "indigo", moat: "Speech-to-text for vernacular context.", verdict: "Niche stability in local language heuristics.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Audio processing layer." }, { factor: "H-Accretion", score: 4, rationale: "Localized NLP/ASR models." }, { factor: "W-Sovereignty", score: 3.5, rationale: "Embedded in call centers." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 3, rationale: "Open-source voice is commoditizing." }, { factor: "L-Scaling", score: 2, rationale: "API-driven." }, { factor: "O-Dependency", score: 0.5, rationale: "Independent logic." }],
    currentMultiple: "3x", targetMultiple: "4x", alphaDelta: "+1x", intervention: "Forensic Pivot.", matchmaking: [{ buyer: "Reliance Jio", gap: "9.8", logic: "Vernacular Artery", play: "Lock in regional context." }]
  },
  { 
    company: "Peritus.ai", domain: "peritus.ai", sector: "IT Services", layer: "Reasoning", ms: 3.5, rs: 3.2, quadrant: "Spines", color: "blue", moat: "Multi-vendor IT log correlation libraries.", verdict: "Execute a Forensic Pivot from technical content to autonomic ops.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Ingests existing cloud logs." }, { factor: "H-Accretion", score: 4.5, rationale: "Domain-expert networking knowledge graphs." }, { factor: "W-Sovereignty", score: 4, rationale: "Critical for DevOps troubleshooting." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 4.5, rationale: "LLMs natively parse and troubleshoot code/logs." }, { factor: "L-Scaling", score: 2, rationale: "Requires integrations." }, { factor: "O-Dependency", score: 3, rationale: "GitHub/StackOverflow data reliance." }],
    currentMultiple: "5x", targetMultiple: "8x", alphaDelta: "+3x", intervention: "Transition from advisor to execution agent.", pivotPath: "Pivot to Active Self-Healing Network Engine."
  },
  { 
    company: "Whodat", domain: "whodat.com", sector: "Spatial Tech", layer: "Reasoning", ms: 4.1, rs: 2.9, quadrant: "Spines", color: "amber", moat: "Low-latency spatial localization algorithms (SLAM).", verdict: "Embed as the ambient orientation middleware layer.",
    moatBreakdown: [{ factor: "P-Foundation", score: 3, rationale: "Utilizes standard camera hardware." }, { factor: "H-Accretion", score: 5, rationale: "Edge-based spatial mapping heuristics." }, { factor: "W-Sovereignty", score: 4.5, rationale: "Core navigation OS for robotics." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 3, rationale: "Vision-Language Models are catching up." }, { factor: "L-Scaling", score: 3, rationale: "Physical deployment mapping required." }, { factor: "O-Dependency", score: 2.5, rationale: "Hardware compute constraints." }],
    currentMultiple: "6x", targetMultiple: "10x", alphaDelta: "+4x", intervention: "Anchor into industrial warehouse automation.", pivotPath: "Transition from AR logic to Robotic Fleet OS."
  },
  { 
    company: "Growthpal", domain: "growthpal.com", sector: "FinTech", layer: "Reasoning", ms: 2.8, rs: 3.4, quadrant: "Dead Zone", color: "blue", moat: "M&A target recommendation engine.", verdict: "High logical commoditization; requires heuristic harvest.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Aggregates public firmographic data." }, { factor: "H-Accretion", score: 3, rationale: "Pattern-matching algorithm." }, { factor: "W-Sovereignty", score: 3, rationale: "Discovery tool; not execution spine." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 4, rationale: "LLMs natively replicate target discovery." }, { factor: "L-Scaling", score: 3, rationale: "Requires human analysts." }, { factor: "O-Dependency", score: 3, rationale: "Third-party API ingestion." }],
    currentMultiple: "4x", targetMultiple: "2x (At Risk)", alphaDelta: "-2x", intervention: "Forensic Exit. Vectorize 'Closed-Deal' heuristics.", matchmaking: [{ buyer: "PitchBook", gap: "9.4", logic: "Reasoning Lock-in", play: "Heuristic Integration." }]
  },
  { 
    company: "Gooru", domain: "gooru.org", sector: "EdTech", layer: "Reasoning", ms: 2.0, rs: 4.8, quadrant: "Dead Zone", color: "rose", moat: "Adaptive student pacing and learning trajectory maps.", verdict: "Extract cognitive engagement data maps and exit.",
    moatBreakdown: [{ factor: "P-Foundation", score: 1, rationale: "No proprietary hardware origin." }, { factor: "H-Accretion", score: 2.5, rationale: "Learning path heuristics." }, { factor: "W-Sovereignty", score: 2.5, rationale: "Easily replaced by school districts." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 5, rationale: "Generalized educational models outpace custom logic." }, { factor: "L-Scaling", score: 4, rationale: "Heavy curriculum customization services." }, { factor: "O-Dependency", score: 5, rationale: "Dependent on school LMS infrastructure." }],
    currentMultiple: "2x", targetMultiple: "0x", alphaDelta: "-2x", intervention: "Friction path from generalized models.", matchmaking: [{ buyer: "Pearson", gap: "8.8", logic: "Data Absorption", play: "Harvest telemetry." }]
  },
  { 
    company: "Worxogo", domain: "worxogo.com", sector: "Prof Services", layer: "Reasoning", ms: 2.4, rs: 4.1, quadrant: "Dead Zone", color: "indigo", moat: "Behavioral science 'nudge' data.", verdict: "High risk; must pivot to an Operational Logic Spine.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Sits on top of CRM data." }, { factor: "H-Accretion", score: 3, rationale: "Proprietary behavioral maps." }, { factor: "W-Sovereignty", score: 2.5, rationale: "High substitutability." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 5, rationale: "Agentic LLMs map intent natively." }, { factor: "L-Scaling", score: 3, rationale: "Human context-setting required." }, { factor: "O-Dependency", score: 3.5, rationale: "Enterprise OS ecosystem reliance." }],
    currentMultiple: "3x", targetMultiple: "1x (At Risk)", alphaDelta: "-2x", intervention: "Forensic Pivot.", matchmaking: [{ buyer: "SAP", gap: "9.8", logic: "Terminal Disruption", play: "Feature Absorption." }]
  },
  { 
    company: "Bewgle", domain: "bewgle.com", sector: "Prof Services", layer: "Reasoning", ms: 2.2, rs: 4.5, quadrant: "Dead Zone", color: "rose", moat: "Unstructured consumer text sentiment extraction.", verdict: "Immediately harvest textual intent datasets to wrap value.",
    moatBreakdown: [{ factor: "P-Foundation", score: 1.5, rationale: "Scrapes public review data." }, { factor: "H-Accretion", score: 2.5, rationale: "NLP text extraction patterns." }, { factor: "W-Sovereignty", score: 2.5, rationale: "Marketing dashboard utility." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 5, rationale: "High exposure to frontier multi-modal systems." }, { factor: "L-Scaling", score: 3, rationale: "Custom dashboard builds." }, { factor: "O-Dependency", score: 5, rationale: "E-commerce platform APIs." }],
    currentMultiple: "2.5x", targetMultiple: "1x (At Risk)", alphaDelta: "-1.5x", intervention: "Harvest datasets.", matchmaking: [{ buyer: "Qualtrics", gap: "9.0", logic: "Sentiment Ingestion", play: "Data tuck-in." }]
  },

  // L4: OUTCOME (INTENT)
  { 
    company: "Mason", domain: "getmason.io", sector: "Retail", layer: "Outcome", ms: 3.8, rs: 3.2, quadrant: "Spines", color: "purple", moat: "Autonomous AI merchandising & checkout agent framework.", verdict: "Vectorize as the terminal intent execution engine for digital storefronts.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Sits on top of existing e-commerce platforms (Shopify/Magento)." }, { factor: "H-Accretion", score: 4.5, rationale: "Proprietary zero-party intent mapping and autonomous merchandising heuristics." }, { factor: "W-Sovereignty", score: 5, rationale: "Deeply embedded in the critical path of revenue generation and checkout." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 4, rationale: "Shopify's native AI features could absorb basic agentic storefront logic." }, { factor: "L-Scaling", score: 2, rationale: "Highly scalable SaaS/API model." }, { factor: "O-Dependency", score: 4, rationale: "Extreme reliance on underlying e-commerce infrastructure platforms." }],
    currentMultiple: "8x", targetMultiple: "14x", alphaDelta: "+6x", intervention: "Evolve from storefront copilot to Autonomous Revenue OS.", pivotPath: "Pivot to an Outcome-Based Autonomous Merchandising Yield."
  },
  { 
    company: "FlyNava Technologies", domain: "flynava.ai", sector: "Mobility", layer: "Outcome", ms: 3.7, rs: 3.1, quadrant: "Spines", color: "indigo", moat: "High-velocity airline ticket dynamic pricing rules.", verdict: "Vectorize internal pricing arbitrage heuristics.",
    moatBreakdown: [{ factor: "P-Foundation", score: 3, rationale: "Direct integration into airline GDS." }, { factor: "H-Accretion", score: 4.5, rationale: "Deep airline margin optimization engines." }, { factor: "W-Sovereignty", score: 3.5, rationale: "Core revenue execution logic." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 3.5, rationale: "Algorithmic pricing is a heavy AI battleground." }, { factor: "L-Scaling", score: 3, rationale: "Airline sales cycles are notoriously slow." }, { factor: "O-Dependency", score: 3, rationale: "Legacy GDS systems (Amadeus, Sabre)." }],
    currentMultiple: "6x", targetMultiple: "10x", alphaDelta: "+4x", intervention: "Execute real-time margin capture rather than selling software seats.", pivotPath: "Pivot to Autonomous Airline Yield Trader."
  },
  { 
    company: "B2Brain", domain: "b2brain.com", sector: "Enterprise Tech", layer: "Outcome", ms: 3.4, rs: 3.6, quadrant: "Spines", color: "purple", moat: "Account-specific buying signal discovery engines.", verdict: "Transition from dashboard to autonomous generator.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Ingests public corporate filings and news." }, { factor: "H-Accretion", score: 4, rationale: "Executive intent tracking maps." }, { factor: "W-Sovereignty", score: 4, rationale: "Feeds the top of the sales funnel." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 4.5, rationale: "LLM agents perfectly replicate research tasks." }, { factor: "L-Scaling", score: 2.5, rationale: "Requires ongoing data pipeline maintenance." }, { factor: "O-Dependency", score: 4, rationale: "CRM lock-in (Salesforce)." }],
    currentMultiple: "4x", targetMultiple: "7x", alphaDelta: "+3x", intervention: "Automate outbound execution.", pivotPath: "Transition to Autonomous Pipeline Agent."
  },
  { 
    company: "Zapty", domain: "zapty.com", sector: "Enterprise Tech", layer: "Outcome", ms: 1.8, rs: 4.9, quadrant: "Dead Zone", color: "rose", moat: "Multi-tenant team task coordination workflows.", verdict: "Extreme redundancy risk against native communication suites.",
    moatBreakdown: [{ factor: "P-Foundation", score: 1, rationale: "No proprietary data origin." }, { factor: "H-Accretion", score: 1.5, rationale: "Basic task state management." }, { factor: "W-Sovereignty", score: 3, rationale: "Sticky but highly vulnerable to consolidation." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 5, rationale: "Replaced by Microsoft Teams / Slack native AI." }, { factor: "L-Scaling", score: 4, rationale: "High marketing CAC in crowded space." }, { factor: "O-Dependency", score: 5, rationale: "Complete ecosystem vulnerability." }],
    currentMultiple: "1.5x", targetMultiple: "0x", alphaDelta: "-1.5x", intervention: "Initiate immediate harvest of group coordination telemetry.", matchmaking: [{ buyer: "Atlassian", gap: "8.5", logic: "User Base", play: "Acqui-hire and sunset." }]
  },
  { 
    company: "ClearTrust", domain: "cleartrust.cc", sector: "Ad-Tech", layer: "Outcome", ms: 3.6, rs: 3.3, quadrant: "Spines", color: "cyan", moat: "Ad-fraud prevention logic.", verdict: "Integrate into Trust-Perimeter for media ecosystems.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Sits on the data flow of ad networks." }, { factor: "H-Accretion", score: 4, rationale: "Traffic analysis and bot-detection heuristics." }, { factor: "W-Sovereignty", score: 4.5, rationale: "Critical perimeter node protecting ROI." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 4, rationale: "AI-generated traffic vs detection arms race." }, { factor: "L-Scaling", score: 3, rationale: "Requires constant updating." }, { factor: "O-Dependency", score: 2, rationale: "Programmatic ad exchange structures." }],
    currentMultiple: "5x", targetMultiple: "8x", alphaDelta: "+3x", intervention: "Build proprietary detection models.", pivotPath: "Transition from Detection to Sovereign Trust Artery."
  },
  { 
    company: "Convertcart", domain: "convertcart.com", sector: "Retail", layer: "Outcome", ms: 2.9, rs: 2.4, quadrant: "Harvest", color: "amber", moat: "Real-time browser clickstream performance loops.", verdict: "Stabilize as a reliable, lean operational utility.",
    moatBreakdown: [{ factor: "P-Foundation", score: 2, rationale: "Browser-side script execution." }, { factor: "H-Accretion", score: 3.5, rationale: "Micro-conversion optimization rules." }, { factor: "W-Sovereignty", score: 3, rationale: "Revenue driving, but easily swapped." }],
    riskBreakdown: [{ factor: "L-Commoditization", score: 3, rationale: "Shopify native AI is absorbing these features." }, { factor: "L-Scaling", score: 2, rationale: "SaaS scaling." }, { factor: "O-Dependency", score: 2.5, rationale: "E-commerce platform architectures." }],
    currentMultiple: "3.5x", targetMultiple: "4.5x", alphaDelta: "+1x", intervention: "Prepare for clean packaging and trade-exit.", matchmaking: [{ buyer: "Adobe / Salesforce", gap: "9.2", logic: "Marketing Cloud", play: "Utility Tuck-in." }]
  }
];

const FUSION_COLORS = {
  emerald: {
    card: 'border-emerald-500/80 shadow-[0_0_30px_rgba(16,185,129,0.2)] bg-emerald-950/20 scale-[1.02] z-20 ring-1 ring-emerald-500/50',
    badge: 'bg-emerald-500 text-slate-950',
    line: 'bg-emerald-500 shadow-[0_0_10px_#10b981] z-20',
    iconBg: 'bg-emerald-500/20 border-emerald-500/50 shadow-[0_0_20px_rgba(16,185,129,0.4)] text-emerald-400',
    box: 'bg-emerald-500/10 border-emerald-500/30 shadow-[0_0_40px_rgba(16,185,129,0.15)]',
    text: 'text-emerald-400',
    textMuted: 'text-emerald-200/80',
    dot: 'bg-emerald-500',
    scoreBg: 'bg-emerald-500/20 border-emerald-500/30',
    border: 'border-emerald-500/50'
  },
  indigo: {
    card: 'border-indigo-500/80 shadow-[0_0_30px_rgba(99,102,241,0.2)] bg-indigo-950/20 scale-[1.02] z-20 ring-1 ring-indigo-500/50',
    badge: 'bg-indigo-500 text-slate-950',
    line: 'bg-indigo-500 shadow-[0_0_10px_#6366f1] z-20',
    iconBg: 'bg-indigo-500/20 border-indigo-500/50 shadow-[0_0_20px_rgba(99,102,241,0.4)] text-indigo-400',
    box: 'bg-indigo-500/10 border-indigo-500/30 shadow-[0_0_40px_rgba(99,102,241,0.15)]',
    text: 'text-indigo-400',
    textMuted: 'text-indigo-200/80',
    dot: 'bg-indigo-500',
    scoreBg: 'bg-indigo-500/20 border-indigo-500/30',
    border: 'border-indigo-500/50'
  },
  cyan: {
    card: 'border-cyan-500/80 shadow-[0_0_30px_rgba(6,182,212,0.2)] bg-cyan-950/20 scale-[1.02] z-20 ring-1 ring-cyan-500/50',
    badge: 'bg-cyan-500 text-slate-950',
    line: 'bg-cyan-500 shadow-[0_0_10px_#06b6d4] z-20',
    iconBg: 'bg-cyan-500/20 border-cyan-500/50 shadow-[0_0_20px_rgba(6,182,212,0.4)] text-cyan-400',
    box: 'bg-cyan-500/10 border-cyan-500/30 shadow-[0_0_40px_rgba(6,182,212,0.15)]',
    text: 'text-cyan-400',
    textMuted: 'text-cyan-200/80',
    dot: 'bg-cyan-500',
    scoreBg: 'bg-emerald-500/20 border-emerald-500/30', 
    border: 'border-cyan-500/50'
  },
  amber: {
    card: 'border-amber-500/80 shadow-[0_0_30px_rgba(245,158,11,0.2)] bg-amber-950/20 scale-[1.02] z-20 ring-1 ring-amber-500/50',
    badge: 'bg-amber-500 text-slate-950',
    line: 'bg-amber-500 shadow-[0_0_10px_#f59e0b] z-20',
    iconBg: 'bg-amber-500/20 border-amber-500/50 shadow-[0_0_20px_rgba(245,158,11,0.4)] text-amber-400',
    box: 'bg-amber-500/10 border-amber-500/30 shadow-[0_0_40px_rgba(245,158,11,0.15)]',
    text: 'text-amber-400',
    textMuted: 'text-amber-200/80',
    dot: 'bg-amber-500',
    scoreBg: 'bg-amber-500/20 border-amber-500/30',
    border: 'border-amber-500/50'
  },
  purple: {
    card: 'border-purple-500/80 shadow-[0_0_30px_rgba(168,85,247,0.2)] bg-purple-950/20 scale-[1.02] z-20 ring-1 ring-purple-500/50',
    badge: 'bg-purple-500 text-slate-950',
    line: 'bg-purple-500 shadow-[0_0_10px_#a855f7] z-20',
    iconBg: 'bg-purple-500/20 border-purple-500/50 shadow-[0_0_20px_rgba(168,85,247,0.4)] text-purple-400',
    box: 'bg-purple-500/10 border-purple-500/30 shadow-[0_0_40px_rgba(168,85,247,0.15)]',
    text: 'text-purple-400',
    textMuted: 'text-purple-200/80',
    dot: 'bg-purple-500',
    scoreBg: 'bg-purple-500/20 border-purple-500/30',
    border: 'border-purple-500/50'
  },
  rose: {
    card: 'border-rose-500/80 shadow-[0_0_30px_rgba(244,63,94,0.2)] bg-rose-950/20 scale-[1.02] z-20 ring-1 ring-rose-500/50',
    badge: 'bg-rose-500 text-slate-950',
    line: 'bg-rose-500 shadow-[0_0_10px_#f43f5e] z-20',
    iconBg: 'bg-rose-500/20 border-rose-500/50 shadow-[0_0_20px_rgba(244,63,94,0.4)] text-rose-400',
    box: 'bg-rose-500/10 border-rose-500/30 shadow-[0_0_40px_rgba(244,63,94,0.15)]',
    text: 'text-rose-400',
    textMuted: 'text-rose-200/80',
    dot: 'bg-rose-500',
    scoreBg: 'bg-rose-500/20 border-rose-500/30',
    border: 'border-rose-500/50'
  }
};

const LENS_CATEGORIES = [
  { id: 'vertical', name: 'Cross-Layer Alpha Loops', label: '1. Cross-Layer Alpha Loops', icon: ArrowUpRight, color: 'emerald', description: 'Synthesizing assets across multiple structural layers (L1-L4) to create end-to-end, autonomous engines that own both the substrate and the reasoning.' },
  { id: 'horizontal', name: 'Horizontal Asset Interlocks', label: '2. Horizontal Asset Interlocks', icon: Shield, color: 'indigo', description: 'Linking multiple assets within the same structural layer to dominate a specific functional capability.' },
  { id: 'resilience', name: 'Sovereign Resilience', label: '3. Sovereign Resilience', icon: Anchor, color: 'amber', description: 'Geopolitical moats. Decoupling the portfolio from foreign hardware, cloud, or spectrum dependencies.' },
  { id: 'efficiency', name: 'Agentic Efficiency', label: '4. Agentic Efficiency', icon: Factory, color: 'cyan', description: 'Margin expansion. Deleting linear human labor costs by deploying autonomous software logic.' }
];

const FUSION_OPPORTUNITIES = {
  vertical: [
    {
      id: 'v1',
      shortName: 'STM',
      name: 'Sovereign Transport Mesh (STM)',
      firms: ['HrdWyr', 'Numocity', 'BluJ Aero', 'SmartShip'],
      score: '9.8 / 10',
      multiple: '24x+',
      thesis: 'Fusing [HrdWyr] on-device silicon with [BluJ Aero] physical payloads, [SmartShip] telemetry, and [Numocity] EV grids to build a kinetic transit network decoupled from public clouds.',
      color: 'emerald',
      icon: Truck
    },
    {
      id: 'v2',
      shortName: 'BSL',
      name: 'The Biosecure Ledger (BSL)',
      firms: ['K-Cat', 'Ahammune', 'Litemed', 'Accelequant'],
      score: '9.5 / 10',
      multiple: '20x',
      thesis: 'Creating an unhackable therapeutic pipeline: [K-Cat] and [Ahammune] bio-digital synthesis locked onto [Litemed] clinical instrumentation, routed through [Accelequant] quantum architecture.',
      color: 'emerald',
      icon: Lock
    },
    {
      id: 'v3',
      shortName: 'SHI',
      name: 'Self-Healing Infrastructure (SHI)',
      firms: ['Nimesa', 'Lavelle Networks', 'LatentForce'],
      score: '8.5 / 10',
      multiple: '15x',
      thesis: 'A Ghost in the Machine for IT. [Nimesa] ensures state recovery, [Lavelle Networks] re-routes traffic, and [LatentForce] resolves root causes autonomously.',
      color: 'emerald',
      icon: Activity
    }
  ],
  horizontal: [
    {
      id: 'h1',
      shortName: 'SPC',
      name: 'Silicon Prototyping Core (SPC)',
      firms: ['HrdWyr', 'Simyog', 'Phyx44'],
      score: '9.7 / 10',
      multiple: '26x+',
      thesis: 'Compressing deep-tech capital expenditure. Validating [Phyx44] clean-energy substrates and [HrdWyr] silicon layouts natively within [Simyog] virtual electro-magnetic physics laboratories before fabrication.',
      color: 'emerald',
      icon: Cpu
    },
    {
      id: 'h2',
      shortName: 'TNP',
      name: 'Trust Network Perimeter (TNP)',
      firms: ['ClearTrust', 'InfiSecure', 'Lavelle Networks'],
      score: '9.4 / 10',
      multiple: '20x',
      thesis: 'A sovereign firewall. Owning Traffic Verification [ClearTrust], Bot Mitigation [InfiSecure], and Enterprise Routing [Lavelle Networks] to stop malignant intent at the absolute edge.',
      color: 'indigo',
      icon: Shield
    },
    {
      id: 'h3',
      shortName: 'BDS',
      name: 'Bio-Digital Discovery Spine (BDS)',
      firms: ['K-Cat', 'Ahammune', 'ManageArtworks'],
      score: '9.6 / 10',
      multiple: '25x',
      thesis: 'An immutable assembly line for synthetic biology. [K-Cat] simulates quantum enzyme mutations, [Ahammune] maps biological disease-pathways, and [ManageArtworks] locks down the terminal FDA regulatory packaging workflow.',
      color: 'cyan',
      icon: Activity
    },
    {
      id: 'h4',
      shortName: 'ACE',
      name: 'Autonomous Commerce Engine (ACE)',
      firms: ['Convertcart', 'Mason', 'ClearTrust'],
      score: '9.2 / 10',
      multiple: '18x',
      thesis: 'Fusing [ClearTrust] verified traffic routing with [Convertcart] micro-conversion rules and [Mason] autonomous storefront agents to create a fully sovereign revenue execution loop.',
      color: 'purple',
      icon: ShoppingBag
    }
  ],
  resilience: [
    {
      id: 'r1',
      shortName: 'NTN',
      name: 'National Trust Network (NTN)',
      firms: ['Okulo Aerospace', 'Lavelle Networks', 'Spanugo'],
      score: '9.5 / 10',
      multiple: '25x+',
      thesis: 'Establishing an off-grid aerial telemetry spine [Okulo Aerospace] feeding into sovereign enterprise routing [Lavelle Networks] with automated regulatory baselines [Spanugo].',
      color: 'amber',
      icon: Radio
    },
    {
      id: 'r2',
      shortName: 'KDS',
      name: 'Kinetic Defense Spine (KDS)',
      firms: ['BluJ Aero', 'Okulo Aerospace', 'Simyog', 'Phyx44'],
      score: '9.6 / 10',
      multiple: '22x',
      thesis: 'A self-sufficient defense layer: Aerial transport [BluJ Aero] and monitoring [Okulo Aerospace] powered by clean-energy hydrogen substrates [Phyx44] designed in virtual physics labs [Simyog].',
      color: 'amber',
      icon: MapPin
    }
  ],
  efficiency: [
    {
      id: 'e1',
      shortName: 'ACF',
      name: 'Agentic Compliance Factory (ACF)',
      firms: ['ManageArtworks', 'Spanugo', 'InfiSecure'],
      score: '9.3 / 10',
      multiple: '18x',
      thesis: 'Transforming manual audit into a Continuous Compliance Engine by wrapping [ManageArtworks] terminal workflows in [Spanugo] multi-cloud constraints and [InfiSecure] traffic loops.',
      color: 'cyan',
      icon: Factory
    },
    {
      id: 'e2',
      shortName: 'EIC',
      name: 'Edge Intelligence Core (EIC)',
      firms: ['HrdWyr', 'Saigeware', 'Mihup'],
      score: '9.4 / 10',
      multiple: '20x',
      thesis: 'Burning [Mihup] vernacular reasoning and [Saigeware] protective traffic screening directly onto [HrdWyr] local AISoC hardware for ultra-secure, low-latency consumer devices.',
      color: 'cyan',
      icon: Cpu
    }
  ]
};

const quadrantTactics = [
  {
    id: "Titans",
    name: "Titans",
    position: "Top-Right",
    color: "emerald",
    icon: Shield,
    implication: "Market Backbones. High Moat + High Resistance. These firms own 'Origin Nodes' (Physics, Spectrum, Hardware) that AI cannot disrupt.",
    tacticalPlay: "Aggressive Market Capture. Focus on GTM velocity and ecosystem dominance. Position as Platform Acquirers."
  },
  {
    id: "Spines",
    name: "Spines",
    position: "Bottom-Right",
    color: "purple",
    icon: Command,
    implication: "Sticky but Threatened. High Moat + Low Resistance. Deeply embedded in workflows but proprietary reasoning is being commoditized by Frontier LLMs.",
    tacticalPlay: "The Sovereign Pivot. Vectorize private workflow context to transform from 'Tool' into an indispensable Industry Brain."
  },
  {
    id: "Harvest",
    name: "Harvest",
    position: "Top-Left",
    color: "amber",
    icon: Target,
    implication: "Niche Utilities. Low Moat + High Resistance. Solve specific, unsexy problems that LLMs don't natively care to solve.",
    tacticalPlay: "Margin Optimization. Treat as cash-yield assets. Keep lean and look for a clean Trade Exit to legacy incumbents."
  },
  {
    id: "Dead Zone",
    name: "Dead Zone",
    position: "Bottom-Left",
    color: "rose",
    icon: AlertTriangle,
    implication: "The Wrapper Trap. Low Moat + Low Resistance. High risk of being replaced by native features in generalized LLMs or OS platforms.",
    tacticalPlay: "Forensic Exit. Execute a 'Heuristic Harvest' (extract unique data) and find a strategic exit before the multiple collapses."
  }
];

// --- STANDARDIZED MANDATE HEADER (HUD) ---
const MandateHeader = ({ title, metric, pulseLabel, pulseText, pulseColor = "indigo" }) => (
  <div className="flex flex-col gap-1.5 mb-6 border-b border-slate-800/60 pb-4 w-full relative z-10">
    <div className="flex flex-wrap items-end gap-3">
       <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tighter leading-none">{title}</h2>
       {metric && (
         <span className="px-2 py-0.5 rounded bg-slate-900/80 border border-slate-700/80 text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5 shadow-inner">
           {metric}
         </span>
       )}
    </div>
    <div className="flex items-start sm:items-center gap-2 mt-1">
       <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-${pulseColor}-500/10 border border-${pulseColor}-500/30 text-${pulseColor}-400 shrink-0`}>
         {pulseLabel}
       </span>
       <span className="text-xs text-slate-300 font-medium leading-snug">{pulseText}</span>
    </div>
  </div>
);

const FirmLogo = ({ domain, company, className }) => {
  const [error, setError] = useState(false);
  
  useEffect(() => {
    setError(false);
  }, [domain]);
  
  if (!domain || error) {
    return (
      <div className={`flex items-center justify-center bg-slate-800 border border-slate-700 shrink-0 ${className}`}>
        <Rocket size="65%" className="text-slate-400" />
      </div>
    );
  }
  
  return (
    <img 
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} 
      alt={company}
      className={`bg-white/90 p-[1px] shrink-0 object-contain ${className}`}
      onError={() => setError(true)}
    />
  );
};

const PortfolioCard = ({ company, domain, sector, ms, rs, moat, verdict, quadrant, activeFusion, isFused, fusionColor, fusionShortName }) => {
  const isDimmed = activeFusion && !isFused;
  const styles = fusionColor ? FUSION_COLORS[fusionColor] : null;

  return (
    <div className={`group relative border rounded-xl p-5 transition-all duration-500 flex flex-col h-full
      ${isDimmed ? 'opacity-20 grayscale blur-[1px] scale-[0.98] border-slate-800 pointer-events-none bg-slate-900/60' : ''}
      ${isFused ? `${styles.card} -translate-y-1 shadow-2xl` : (!isDimmed ? 'bg-slate-900/60 border-slate-800 hover:border-indigo-500/50 hover:-translate-y-0.5 shadow-lg' : '')}
    `}>
      {isFused && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${styles.badge} text-[8px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-lg z-30 animate-pulse flex items-center gap-1.5`}>
          <Workflow size={10} /> {fusionShortName ? `${fusionShortName} NODE` : 'ACTIVE NODE'}
        </div>
      )}
      <div className="flex justify-between items-start mb-4">
        <div className="flex flex-col gap-1.5">
          <h4 className="text-white font-bold text-base tracking-tight flex items-center gap-2">
            <FirmLogo domain={domain} company={company} className="w-4 h-4 rounded-sm" />
            {company}
          </h4>
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full border w-fit text-[8px] font-black uppercase tracking-widest bg-slate-800/50 text-slate-400 border-slate-700">
            {sector}
          </div>
        </div>
        <div className={`px-1.5 py-0.5 rounded text-[7px] font-black uppercase tracking-widest border shrink-0 ${
          quadrant === 'Titans' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 
          quadrant === 'Spines' ? 'bg-purple-500/10 text-purple-400 border-purple-500/30' : 
          quadrant === 'Harvest' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 
          'bg-rose-500/10 text-rose-400 border-rose-500/30'
        }`}>
          {quadrant}
        </div>
      </div>
      <div className="space-y-4 flex-1">
        <div>
          <span className="text-[8px] font-black uppercase text-slate-500 tracking-tighter">Strategic Moat (M<sub>s</sub>)</span>
          <p className="text-[11px] text-slate-300 leading-relaxed mt-1 font-medium">{moat}</p>
        </div>
        <div className="pt-3 border-t border-slate-800 mt-auto">
          <span className="text-[8px] font-black uppercase text-indigo-400 tracking-widest flex items-center justify-between">
            <span className="flex items-center gap-1.5"><Cpu size={10} /> Frontier Verdict</span>
            <span className="text-[6px] text-slate-500">MULTI-AGENT CHAIN</span>
          </span>
          <p className="text-[11px] text-indigo-200/80 leading-relaxed mt-1.5 italic font-bold">
            "{verdict}"
          </p>
        </div>
      </div>
    </div>
  );
};

const StackLayer = ({ title, icon: Icon, color, layerName, description, metric, activeFusion, fusionData, isLast }) => {
  const companies = PortfolioData.filter(f => f.layer === layerName);
  if (companies.length === 0) return null;

  const hasFusionFirm = activeFusion && companies.some(c => fusionData.firms.includes(c.company));
  const styles = hasFusionFirm ? FUSION_COLORS[fusionData.color] : null;

  return (
    <div className={`relative mb-14 last:mb-0 animate-in fade-in slide-in-from-bottom-2 duration-500 ${activeFusion && !hasFusionFirm ? 'opacity-40' : ''}`}>
      <div className="flex items-center gap-4 mb-6 relative z-10">
        <div className={`p-3.5 rounded-2xl transition-all duration-500 ${hasFusionFirm ? styles.iconBg : `bg-${color}-500/10 border border-${color}-500/20 shadow-[0_0_20px_rgba(0,0,0,0.3)] text-${color}-400`}`}>
          <Icon size={24} />
        </div>
        <div>
          <h3 className={`text-xl font-black uppercase tracking-tighter transition-colors duration-500 ${hasFusionFirm ? 'text-white' : 'text-white'}`}>{title}</h3>
          <p className="text-xs text-slate-400 font-medium">{description}</p>
        </div>
        <div className="ml-auto text-right hidden lg:block">
          <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Focus Metric</div>
          <div className={`text-base font-bold transition-colors duration-500 ${hasFusionFirm ? styles.text : `text-${color}-400`}`}>{metric}</div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 relative z-10">
        {companies.map((c, i) => (
          <PortfolioCard 
            key={i} 
            {...c} 
            activeFusion={activeFusion}
            isFused={activeFusion && fusionData.firms.includes(c.company)}
            fusionColor={activeFusion ? fusionData.color : null}
            fusionShortName={activeFusion ? fusionData.shortName : null}
          />
        ))}
      </div>
      
      <div className={`absolute left-8 top-full h-14 w-px transition-all duration-500 ${hasFusionFirm ? styles.line : 'bg-gradient-to-b from-slate-800 to-transparent'} ${isLast ? 'hidden' : ''}`}></div>
    </div>
  );
};

const CoverView = ({ onStartBriefing }) => {
  return (
    <div className="h-full w-full flex flex-col justify-center p-8 lg:p-32 relative overflow-hidden animate-in fade-in duration-1000 bg-[#020617]">
      {/* Background Grid Layer */}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{ 
        backgroundImage: `linear-gradient(to right, #1e293b 1px, transparent 1px), linear-gradient(to bottom, #1e293b 1px, transparent 1px)`,
        backgroundSize: '80px 80px' 
      }}></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-[#020617] via-transparent to-indigo-500/5 pointer-events-none"></div>
      
      <div className="max-w-7xl w-full mx-auto relative z-10 flex flex-col items-start">
         {/* Hero Branding */}
         <div className="flex flex-col mb-16 pt-12">
            {/* Institutional Eyebrow */}
            <div className="flex items-center gap-4 mb-6">
               <div className="w-8 md:w-12 h-px md:h-[2px] bg-indigo-500 shadow-[0_0_10px_#6366f1] shrink-0"></div>
               <div className="flex flex-wrap items-center gap-2 md:gap-3 text-[10px] md:text-xs font-black uppercase tracking-[0.4em] text-slate-400">
                  <span>Asset Owner Briefing</span>
                  <span className="text-indigo-500/50 hidden md:inline">•</span>
                  <span className="text-slate-500 tracking-[0.2em] md:tracking-[0.4em]">CONTEXT-LOCKED // Q2 2026</span>
               </div>
            </div>

            <h1 className="text-6xl md:text-[140px] font-black text-white uppercase tracking-[-0.04em] leading-[0.85] mb-2 drop-shadow-2xl">
               Ideaspring
            </h1>
            <h2 className="text-6xl md:text-[140px] font-black text-slate-500/80 uppercase tracking-[-0.04em] leading-[0.85] mb-2">
               Portfolio
            </h2>
            <h2 className="text-6xl md:text-[140px] font-black text-slate-500/80 uppercase tracking-[-0.04em] leading-[0.85]">
               Underwriting<span className="text-indigo-500">.</span>
            </h2>
         </div>

         {/* Alpha Foundry Architecture Bar */}
         <div className="w-full bg-[#0a0f1e]/80 border border-slate-800/80 rounded-[20px] px-6 py-5 shadow-2xl backdrop-blur-md mb-8">
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6 xl:gap-0">
               
               {/* Context Elements Group */}
               <div className="flex flex-col md:flex-row items-start md:items-center gap-6 xl:gap-8 flex-1">
                  
                  {/* Item 1: Underwriting Protocol */}
                  <div className="flex items-center gap-3 lg:gap-4 shrink-0">
                     <div className="text-cyan-400">
                        <Cpu size={20} className="md:w-6 md:h-6" strokeWidth={1.5} />
                     </div>
                     <div>
                        <div className="text-[7px] md:text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1 whitespace-nowrap">Alpha Protocol</div>
                        <div className="text-xs lg:text-sm font-black text-white uppercase tracking-wider flex items-center gap-2 whitespace-nowrap">
                           Portfolio Underwriting
                        </div>
                     </div>
                  </div>

                  <div className="hidden md:block w-px h-8 bg-slate-800 shrink-0"></div>

                  {/* Item 2: Sector Brains */}
                  <div className="flex items-center gap-3 lg:gap-4 shrink-0">
                     <div className="text-pink-400">
                        <BrainCircuit size={20} className="md:w-6 md:h-6" strokeWidth={1.5} />
                     </div>
                     <div>
                        <div className="text-[7px] md:text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1 whitespace-nowrap">Market Context</div>
                        <div className="text-xs lg:text-sm font-black text-white uppercase tracking-wider flex items-center gap-2 whitespace-nowrap">
                           Sector Brains <CheckCircle2 size={12} className="text-emerald-500 shrink-0" />
                        </div>
                     </div>
                  </div>

                  <div className="hidden md:block w-px h-8 bg-slate-800 shrink-0"></div>

                  {/* Item 3: Ideaspring DNA */}
                  <div className="flex items-center gap-3 lg:gap-4 shrink-0">
                     <div className="text-emerald-400">
                        <Workflow size={20} className="md:w-6 md:h-6" strokeWidth={1.5} />
                     </div>
                     <div>
                        <div className="text-[7px] md:text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1 whitespace-nowrap">Internal Context</div>
                        <div className="text-xs lg:text-sm font-black text-white uppercase tracking-wider flex items-center gap-2 whitespace-nowrap">
                           Ideaspring DNA <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shrink-0"></div>
                        </div>
                     </div>
                  </div>
               </div>

               <div className="hidden xl:block w-px h-8 bg-slate-800 mx-4 lg:mx-8 shrink-0"></div>
               <div className="block xl:hidden h-px w-full bg-slate-800"></div>

               {/* Principals */}
               <div className="flex items-center gap-3 lg:gap-4 shrink-0">
                  <div className="flex -space-x-3 shrink-0">
                     <img src="/images/profile_maheshwar.png" alt="Maheshwar" className="w-8 h-8 md:w-9 md:h-9 rounded-full border-2 border-[#0a0f1e] object-cover shadow-md" />
                     <img src="/images/profile_balbinder.png" alt="Balbinder" className="w-8 h-8 md:w-9 md:h-9 rounded-full border-2 border-[#0a0f1e] object-cover shadow-md" />
                  </div>
                  <div>
                     <div className="text-[7px] md:text-[8px] font-black text-emerald-500 uppercase tracking-widest mb-1 whitespace-nowrap">Principal Orchestration</div>
                     <div className="text-xs lg:text-sm font-black text-white uppercase tracking-wider whitespace-nowrap">Maheshwar & Balbinder</div>
                  </div>
               </div>
               
            </div>
         </div>

         {/* Minimal Action Button */}
         <button 
            onClick={onStartBriefing} 
            className="group flex items-center gap-3 py-2 bg-transparent text-slate-500 hover:text-indigo-400 transition-all duration-300 font-black text-[10px] uppercase tracking-[0.2em] w-fit"
         >
            <span>Access Strategic Audit</span>
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
         </button>
      </div>
      
      {/* Visual Command Watermark */}
      <div className="absolute bottom-12 right-12 opacity-5 pointer-events-none select-none hidden md:block">
         <Command size={220} className="text-white" />
      </div>
    </div>
  );
};

const MatrixView = () => {
  const [hoveredMatrixFirm, setHoveredMatrixFirm] = useState(null);
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [selectedQuadrant, setSelectedQuadrant] = useState(null);
  const [isLedgerExpanded, setIsLedgerExpanded] = useState(false);

  // Auto-expand ledger when a specific firm is selected to show the forensic proof
  useEffect(() => {
    if (selectedFirm) {
      setIsLedgerExpanded(true);
    }
  }, [selectedFirm]);

  // Determine current context for the bottom panel
  const activeFirmObj = PortfolioData.find(f => f.company === selectedFirm);
  const activeQuadrantId = selectedQuadrant || activeFirmObj?.quadrant;
  const activeQuadrantObj = quadrantTactics.find(q => q.id === activeQuadrantId);

  // Dynamic KPI Calculations
  const titansAndSpinesCount = PortfolioData.filter(f => ['Titans', 'Spines'].includes(f.quadrant)).length;
  const harvestCount = PortfolioData.filter(f => f.quadrant === 'Harvest').length;
  const deadZoneCount = PortfolioData.filter(f => f.quadrant === 'Dead Zone').length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      
      <div className="col-span-full">
         <MandateHeader 
            title="CAPITAL ALLOCATION & EXECUTION ROADMAP" 
            metric="4 EXECUTION ZONES" 
            pulseLabel="MANDATE"
            pulseText="Execute Forensic Pivots for 'Spine' assets to protect multiples."
            pulseColor="amber"
         />
      </div>

      {/* Strategic Summary View (KPI Header) */}
      <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div 
          onClick={() => { setSelectedQuadrant(null); setSelectedFirm(null); }}
          className="bg-slate-900/40 border border-slate-800 rounded-3xl p-6 relative overflow-hidden group hover:border-emerald-500/50 transition-all cursor-pointer shadow-lg hover:shadow-[0_0_30px_rgba(16,185,129,0.1)]"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-[40px] group-hover:bg-emerald-500/20 transition-all duration-500"></div>
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-3 flex items-center gap-2 relative z-10">
            <TrendingUp size={14} /> Titans & Spines
          </div>
          <div className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tighter relative z-10">{titansAndSpinesCount}</div>
          <div className="text-[11px] font-bold text-slate-400 relative z-10">Double Down / Pivot Active</div>
        </div>

        <div className="bg-slate-900/40 border border-slate-800 rounded-3xl p-6 relative overflow-hidden group hover:border-indigo-500/50 transition-all cursor-default shadow-lg hover:shadow-[0_0_30px_rgba(99,102,241,0.1)]">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-[40px] group-hover:bg-indigo-500/20 transition-all duration-500"></div>
          <div className="text-[10px] font-black uppercase tracking-widest text-indigo-400 mb-3 flex items-center gap-2 relative z-10">
            <Zap size={14} /> Alpha Potential
          </div>
          <div className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tighter relative z-10">25x</div>
          <div className="text-[11px] font-bold text-slate-400 relative z-10">Peak Target (National Trust)</div>
        </div>

        <div 
          onClick={() => { setSelectedQuadrant('Harvest'); setSelectedFirm(null); }}
          className={`bg-slate-900/40 border border-slate-800 rounded-3xl p-6 relative overflow-hidden group hover:border-amber-500/50 transition-all cursor-pointer shadow-lg hover:shadow-[0_0_30px_rgba(245,158,11,0.1)] ${selectedQuadrant === 'Harvest' ? 'border-amber-500/50 bg-amber-500/5' : ''}`}
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-[40px] group-hover:bg-amber-500/20 transition-all duration-500"></div>
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-3 flex items-center gap-2 relative z-10">
            <Target size={14} /> Harvest Targets
          </div>
          <div className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tighter relative z-10">{harvestCount}</div>
          <div className="text-[11px] font-bold text-slate-400 relative z-10">Existential Matchmaking Required</div>
        </div>

        <div 
          onClick={() => { setSelectedQuadrant('Dead Zone'); setSelectedFirm(null); }}
          className={`bg-slate-900/40 border border-slate-800 rounded-3xl p-6 relative overflow-hidden group hover:border-rose-500/50 transition-all cursor-pointer shadow-lg hover:shadow-[0_0_30px_rgba(244,63,94,0.1)] ${selectedQuadrant === 'Dead Zone' ? 'border-rose-500/50 bg-rose-500/5' : ''}`}
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/5 rounded-full blur-[40px] group-hover:bg-rose-500/20 transition-all duration-500"></div>
          <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-3 flex items-center gap-2 relative z-10">
            <AlertTriangle size={14} /> Dead Zone
          </div>
          <div className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tighter relative z-10">{deadZoneCount}</div>
          <div className="text-[11px] font-bold text-slate-400 relative z-10">Imminent Disruption Risk</div>
        </div>
      </div>

      {/* Contextual Debt Alert - Moved here to act as a preamble to the Matrix */}
      <div className="lg:col-span-12 flex items-center gap-3 px-4 py-2.5 bg-amber-500/5 border border-amber-500/20 rounded-xl -mt-2 mb-2 opacity-80 hover:opacity-100 transition-opacity">
         <div className="w-1.5 h-1.5 rounded-full bg-amber-500/80 animate-pulse shrink-0"></div>
         <div className="text-[10px] text-amber-200/60 font-medium">
            <strong className="text-amber-400/90 uppercase tracking-widest mr-2">Contextual Debt:</strong>
            Scores plotted using public proxy data. Recommend codifying partner intuition into a Sovereign Context Layer to eliminate alpha leakage.
         </div>
      </div>

      <div className="lg:col-span-8 bg-[#030712] border border-slate-800 rounded-[32px] p-8 pb-14 pl-16 md:pl-20 relative overflow-hidden shadow-2xl">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ 
          backgroundImage: 'radial-gradient(circle at 2px 2px, #1e293b 1px, transparent 0)', 
          backgroundSize: '40px 40px' 
        }}></div>
        
        <div className="relative h-[550px] w-full border-l-2 border-b-2 border-slate-700">
          <div className="absolute top-1/2 -left-10 md:-left-12 flex items-center justify-center origin-center -translate-x-1/2 -translate-y-1/2 -rotate-90 z-20">
             <span className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-500 whitespace-nowrap drop-shadow-md">Disruption Resistance (Low → High)</span>
          </div>
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 text-[10px] font-black uppercase tracking-[0.4em] text-slate-500 whitespace-nowrap">Sovereign Moat (Low → High)</div>
          
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-800/50"></div>
          <div className="absolute top-1/2 left-0 right-0 h-px bg-slate-800/50"></div>

          {/* Quadrant Interaction Zones */}
          {quadrantTactics.map(q => (
             <div 
               key={q.id}
               onClick={() => { setSelectedQuadrant(q.id); setSelectedFirm(null); }}
               className={`absolute w-1/2 h-1/2 cursor-pointer transition-all duration-300 ${q.position === 'Top-Right' ? 'top-0 right-0' : q.position === 'Bottom-Right' ? 'bottom-0 right-0' : q.position === 'Top-Left' ? 'top-0 left-0' : 'bottom-0 left-0'} hover:bg-${q.color}-500/5 ${selectedQuadrant === q.id ? `bg-${q.color}-500/5 border border-${q.color}-500/20` : ''}`}
             >
                <div className={`absolute ${q.position.includes('Top') ? 'top-4' : 'bottom-4'} ${q.position.includes('Right') ? 'right-4' : 'left-4'} text-xs font-black text-${q.color}-400/40 uppercase tracking-widest`}>
                   {q.name}
                </div>
             </div>
          ))}

          {/* Dynamic Vectors (The Sovereign Pivot) */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#a855f7" />
              </marker>
            </defs>
            {PortfolioData.filter(f => f.quadrant === 'Spines').map((f, i) => {
              const startX = ((f.ms - 1.5) / 3.5) * 100;
              const resistance = 6 - f.rs;
              const bottom = ((resistance - 1.0) / 4.0) * 100;
              const startY = 100 - bottom;
              const endX = Math.min(95, startX + 18);
              const endY = Math.max(5, startY - 20);
              const isHovered = hoveredMatrixFirm === f.company;
              const isSelected = selectedFirm === f.company;

              return (
                <g key={`vector-${i}`} className={`transition-all duration-500 ${(isHovered || isSelected) ? 'opacity-100 drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]' : 'opacity-20'}`}>
                  <line
                    x1={`${startX}%`} y1={`${startY}%`}
                    x2={`${endX}%`} y2={`${endY}%`}
                    stroke="#a855f7"
                    strokeWidth={(isHovered || isSelected) ? "2.5" : "1.5"}
                    strokeDasharray="4 4"
                    markerEnd="url(#arrowhead)"
                    className={(isHovered || isSelected) ? "animate-[pulse_1s_ease-in-out_infinite]" : ""}
                  />
                </g>
              );
            })}
          </svg>

          {PortfolioData.map((f, i) => {
            const left = ((f.ms - 1.5) / 3.5) * 100;
            const resistance = 6 - f.rs;
            const bottom = ((resistance - 1.0) / 4.0) * 100;
            const isNearTop = bottom > 85;
            const isNearRight = left > 80;
            const isNearLeft = left < 20;
            const tooltipPositionClasses = `${isNearTop ? 'top-full mt-3' : 'bottom-full mb-3'} ${isNearRight ? 'right-0' : isNearLeft ? 'left-0' : 'left-1/2 -translate-x-1/2'}`;
            
            return (
              <div 
                key={i} 
                onClick={(e) => { e.stopPropagation(); setSelectedFirm(f.company); setSelectedQuadrant(null); }}
                className={`absolute group cursor-pointer transition-transform hover:scale-125 z-10 hover:z-50 ${selectedFirm === f.company ? 'scale-150 z-50' : ''}`} 
                style={{ left: `${Math.max(0, Math.min(100, left))}%`, bottom: `${Math.max(0, Math.min(100, bottom))}%` }}
                onMouseEnter={() => setHoveredMatrixFirm(f.company)}
                onMouseLeave={() => setHoveredMatrixFirm(null)}
              >
                <div className={`absolute left-5 top-1/2 -translate-y-1/2 text-[8px] font-bold tracking-wider text-slate-500/80 whitespace-nowrap pointer-events-none group-hover:opacity-0 transition-opacity drop-shadow-md ${selectedFirm === f.company ? 'opacity-0' : ''}`}>
                  {f.company}
                </div>

                <div className={`w-3.5 h-3.5 rounded-full shadow-[0_0_15px_rgba(0,0,0,0.8)] border-2 border-white/20 transition-all group-hover:border-white/50 ${
                  f.quadrant === 'Titans' ? 'bg-emerald-500 shadow-emerald-500/40' : 
                  f.quadrant === 'Spines' ? 'bg-purple-500 shadow-purple-500/40' : 
                  f.quadrant === 'Harvest' ? 'bg-amber-500 shadow-amber-500/40' : 'bg-rose-500 shadow-rose-500/40'
                } ${selectedFirm === f.company ? 'ring-4 ring-white/30' : ''}`}></div>
                
                <div className={`absolute ${tooltipPositionClasses} px-4 py-3 bg-[#020617]/95 backdrop-blur-xl border border-slate-600 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-[0_20px_50px_rgba(0,0,0,0.9)] pointer-events-none min-w-[140px] ${selectedFirm === f.company ? 'opacity-100' : ''}`}>
                  <div className="mb-1 text-indigo-400 text-xs font-bold flex items-center gap-1.5">
                    <FirmLogo domain={f.domain} company={f.company} className="w-3.5 h-3.5 rounded-[3px]" />
                    {f.company}
                  </div>
                  <div className="text-[10px] text-slate-300 font-medium">Moat: {f.ms} <span className="text-slate-600 mx-1.5">|</span> Risk: {f.rs}</div>
                  <div className="text-[8px] text-slate-500 uppercase mt-1.5 font-black tracking-widest">{f.sector}</div>
                  
                  {f.pivotPath && f.quadrant === 'Spines' && (
                     <div className="mt-3 pt-3 border-t border-purple-500/30">
                        <div className="text-[8px] text-purple-400 font-black uppercase tracking-widest flex items-center gap-1.5 mb-1.5">
                           <ArrowUpRight size={10} /> The Sovereign Pivot
                        </div>
                        <div className="text-[10px] text-purple-200/90 whitespace-normal leading-relaxed w-48 font-medium">
                           {f.pivotPath}
                        </div>
                     </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="lg:col-span-4 flex flex-col gap-6">
        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 flex flex-col h-[280px]">
          <h4 className="text-white font-black text-xs uppercase tracking-widest mb-4 flex items-center gap-2">
            <Shield size={16} className="text-indigo-400" /> Scoring Framework
          </h4>
          <div className="space-y-4">
             <div className="flex gap-3">
                <div className="text-indigo-400 font-bold text-xs">M<sub>s</sub></div>
                <div className="text-[10px] text-slate-400 leading-relaxed italic">Calculated via Physical Origin, Heuristic Depth, and Workflow Embedding.</div>
             </div>
             <div className="flex gap-3 border-t border-slate-800 pt-4">
                <div className="text-rose-400 font-bold text-xs">R<sub>s</sub></div>
                <div className="text-[10px] text-slate-400 leading-relaxed italic">Calculated via Logical Commoditization Risk and Scaling Labor Density.</div>
             </div>
          </div>
          <div className="mt-auto p-3 bg-slate-950 border border-slate-800 rounded-xl text-[9px] text-slate-400 italic">
              <span className="font-bold text-emerald-400">Note on Matrix Plotting:</span> The Sovereign Matrix inverts Risk (R<sub>s</sub>) into <strong className="text-white">Disruption Resistance (6 - R<sub>s</sub>)</strong> so highly defensible assets are top-right.
          </div>
        </div>

        <div className="flex-1 overflow-y-auto max-h-[300px] pr-2 space-y-2 custom-scrollbar border border-slate-800 rounded-2xl p-4 bg-slate-900/20">
           <div className="text-[10px] font-black uppercase text-slate-600 mb-3 ml-1 tracking-widest sticky top-0 bg-[#060B1A] py-1 z-10">Live Node Feed</div>
           {PortfolioData.sort((a, b) => b.ms - a.ms).map((f, i) => (
             <div 
               key={i} 
               onClick={() => { setSelectedFirm(f.company); setSelectedQuadrant(null); }}
               className={`cursor-pointer border rounded-xl p-3 flex items-center justify-between transition-all ${selectedFirm === f.company ? 'bg-slate-800 border-indigo-500 shadow-lg' : 'bg-slate-900/40 border-slate-800/40 hover:bg-slate-800/40'}`}
             >
                <div className="flex items-center gap-3 overflow-hidden">
                   <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                     f.quadrant === 'Titans' ? 'bg-emerald-500' : f.quadrant === 'Spines' ? 'bg-purple-500' : f.quadrant === 'Harvest' ? 'bg-amber-500' : f.quadrant === 'Dead Zone' ? 'bg-rose-500' : 'bg-rose-500'
                   }`}></div>
                   <div className="flex items-center gap-2 min-w-0">
                      <FirmLogo domain={f.domain} company={f.company} className="w-3.5 h-3.5 rounded-[3px] shrink-0" />
                      <div className="text-[11px] font-bold text-white tracking-tight truncate">{f.company}</div>
                   </div>
                </div>
                <div className="flex items-center gap-4 shrink-0 pl-2">
                   <div className="text-right">
                      <div className="text-[7px] font-black text-slate-500 uppercase">Moat</div>
                      <div className="text-[10px] font-black text-indigo-400">{f.ms}</div>
                   </div>
                   <div className="text-right">
                      <div className="text-[7px] font-black text-slate-500 uppercase">Risk</div>
                      <div className="text-[10px] font-black text-rose-400">{f.rs}</div>
                   </div>
                </div>
             </div>
           ))}
        </div>
      </div>
      
      {/* Execution Matrix: Zone-Wise Strategic Playbook */}
      <div className="lg:col-span-12 mt-6">
         <div className="flex items-center gap-3 mb-6 relative z-10">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white">Orchestration Protocol: Capital Allocation as Warfare</span>
            <div className="h-px flex-1 bg-slate-800"></div>
         </div>
         
         <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {activeQuadrantObj ? (
               // Dynamic Panel when a firm or quadrant is selected
               <div className={`lg:col-span-4 bg-[#0a0f1e]/80 border border-slate-800 rounded-[32px] p-8 md:p-10 animate-in fade-in slide-in-from-bottom-2 duration-500 relative overflow-hidden group min-h-[550px]`}>
                  <div className={`absolute top-0 right-0 w-96 h-96 bg-${activeQuadrantObj.color}-500/5 rounded-full blur-[100px] pointer-events-none`}></div>
                  
                  <div className="flex flex-col lg:flex-row gap-10 lg:items-start relative z-10 h-full">
                     <div className="flex flex-col gap-6 lg:w-[320px] shrink-0 h-full">
                        <div className="flex items-center gap-4">
                           <div className={`p-4 rounded-2xl bg-${activeQuadrantObj.color}-500/20 border border-${activeQuadrantObj.color}-500/40 text-${activeQuadrantObj.color}-400 shadow-xl`}>
                              <activeQuadrantObj.icon size={32} />
                           </div>
                           <div>
                              <div className={`text-[10px] font-black uppercase tracking-[0.3em] text-${activeQuadrantObj.color}-400 mb-1`}>
                                 {selectedFirm ? 'Target Firm Analysis' : 'Execution Mandate'}
                              </div>
                              <h2 className="text-3xl font-black text-white uppercase tracking-tighter leading-none">
                                 {selectedFirm || activeQuadrantObj.name}
                              </h2>
                           </div>
                        </div>

                        <div className="p-5 bg-slate-950/80 border border-slate-800 rounded-2xl">
                           <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                              <Info size={12} /> Strategic Implication
                           </div>
                           <p className="text-sm text-slate-300 leading-relaxed font-medium">
                              {activeQuadrantObj.implication}
                           </p>
                        </div>
                        
                        <button 
                           onClick={() => { setSelectedFirm(null); setSelectedQuadrant(null); setIsLedgerExpanded(false); }}
                           className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-500 hover:text-white transition-colors w-fit ml-1 mt-auto"
                        >
                           <X size={12} /> Reset Orchestration Matrix
                        </button>
                     </div>

                     <div className="flex-1 flex flex-col gap-8 h-full">
                        <div className="h-full flex flex-col">
                           <div className={`text-[11px] font-black uppercase tracking-widest text-${activeQuadrantObj.color}-400 mb-4 flex items-center gap-2`}>
                              <Target size={14} /> {selectedFirm ? `Forensic Roadmap for ${selectedFirm}` : `Strategic Play: ${activeQuadrantObj.name}`}
                           </div>
                           <div className="text-lg md:text-xl font-bold text-white leading-relaxed mb-6">
                              {activeFirmObj?.intervention || activeQuadrantObj.tacticalPlay}
                           </div>

                           <div className="h-px w-full bg-slate-800/60 mb-8"></div>

                           {/* Matchmaking Panel (Optimized for multiple buyers/insights) */}
                           {['Harvest', 'Dead Zone'].includes(activeQuadrantObj.id) && (
                              <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                                 <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                                    {/* Existential Buyer Map */}
                                    <div className="md:col-span-8 bg-slate-950 border border-slate-800 rounded-3xl p-6 relative overflow-hidden">
                                       <div className="absolute top-0 right-0 p-4 opacity-10"><Search size={40} className={`text-${activeQuadrantObj.color}-500`} /></div>
                                       <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-6 flex justify-between items-center">
                                          <span>Existential Liquidity Heatmap</span>
                                          <span className="text-indigo-400">Exit Premium: Critical</span>
                                       </div>
                                       
                                       {Array.isArray(activeFirmObj?.matchmaking) ? (
                                          <div className="grid grid-cols-1 gap-3">
                                             {activeFirmObj.matchmaking.map((m, idx) => (
                                                <div key={idx} className="group/match bg-slate-900/40 border border-slate-800 rounded-xl p-3 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-indigo-500/50 transition-all">
                                                   <div className="flex items-center gap-4">
                                                      <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center font-black text-xs text-white border border-slate-700">
                                                         {m.gap}
                                                      </div>
                                                      <div>
                                                         <div className="text-xs font-black text-white">{m.buyer}</div>
                                                         <div className="text-[9px] font-bold text-indigo-400/80 uppercase tracking-tight">{m.logic}</div>
                                                      </div>
                                                   </div>
                                                   <div className="md:text-right flex-1 md:max-w-[280px]">
                                                      <div className="text-[10px] text-slate-400 leading-tight italic group-hover/match:text-slate-300 transition-colors">
                                                         "{m.play}"
                                                      </div>
                                                   </div>
                                                </div>
                                             ))}
                                          </div>
                                       ) : activeFirmObj?.matchmaking ? (
                                          <div className="flex flex-col gap-5">
                                             <div className="flex items-center gap-5">
                                                <div className={`w-16 h-16 rounded-2xl bg-${activeQuadrantObj.color}-500/10 border border-${activeQuadrantObj.color}-500/30 flex items-center justify-center font-black text-2xl text-${activeQuadrantObj.color}-400`}>
                                                   {activeFirmObj.matchmaking.gap.split('/')[0]}
                                                </div>
                                                <div>
                                                   <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Target Buyer</div>
                                                   <div className="text-2xl font-black text-white">{activeFirmObj.matchmaking.buyer}</div>
                                                </div>
                                             </div>
                                             <div className="text-sm font-medium text-slate-300 leading-relaxed italic border-l-2 border-indigo-500/50 pl-4">
                                                {activeFirmObj.matchmaking.logic} Gap: Security trade premium before multiple collapse.
                                             </div>
                                          </div>
                                       ) : (
                                          <div className="flex items-center justify-center h-48 border border-dashed border-slate-800 rounded-xl">
                                             <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Select a Node to map Exit Premium</span>
                                          </div>
                                       )}
                                    </div>

                                    {/* Risk Audit Sidebar */}
                                    <div className="md:col-span-4 bg-slate-950 border border-rose-500/20 rounded-3xl p-6 relative overflow-hidden">
                                       <div className="absolute top-0 right-0 p-3 opacity-20"><Flame size={40} className="text-rose-500" /></div>
                                       <div className="text-[9px] font-black uppercase tracking-widest text-rose-500/80 mb-6">Forensic Audit</div>
                                       <div className="space-y-6">
                                          <div className="space-y-2">
                                             <div className="flex justify-between items-center text-[10px] font-black">
                                                <span className="text-slate-400 uppercase">Heuristic Half-Life</span>
                                                <span className="text-rose-400">{activeFirmObj?.rs ? (activeFirmObj.rs * 20).toFixed(0) : '85'}% Decay</span>
                                             </div>
                                             <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                                <div className="h-full bg-rose-500" style={{ width: `${activeFirmObj?.rs ? activeFirmObj.rs * 20 : 85}%` }}></div>
                                             </div>
                                          </div>
                                          
                                          <div className="space-y-3 pt-4 border-t border-slate-800">
                                             <div className="flex items-start gap-3">
                                                <div className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-1 shrink-0"></div>
                                                <p className="text-[10px] text-slate-400 leading-relaxed">Commoditization of core matching logic by Frontier LLMs.</p>
                                             </div>
                                             <div className="flex items-start gap-3">
                                                <div className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-1 shrink-0"></div>
                                                <p className="text-[10px] text-slate-400 leading-relaxed">Platform risk: Upstream API dependency for ingestion.</p>
                                             </div>
                                          </div>

                                          <div className="mt-auto pt-6 border-t border-slate-800 flex justify-between items-center">
                                             <div className="text-[8px] font-black text-rose-400 uppercase tracking-widest flex items-center gap-2">
                                                <AlertTriangle size={10}/> Heuristic Extraction
                                             </div>
                                          </div>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           )}

                           {/* Titan/Spine Pivot Detail */}
                           {['Titans', 'Spines'].includes(activeQuadrantObj.id) && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                                 <div className={`bg-slate-950 border border-${activeQuadrantObj.color}-500/20 rounded-3xl p-6 relative overflow-hidden`}>
                                    <div className="absolute top-0 right-0 p-3 opacity-20"><Zap size={40} className={`text-${activeQuadrantObj.color}-500`} /></div>
                                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-4">Sovereign Pivot Path</div>
                                    {activeFirmObj?.pivotPath ? (
                                       <div className="flex flex-col gap-4">
                                          <div className="flex items-center gap-3">
                                             <div className={`w-8 h-8 rounded-lg bg-${activeQuadrantObj.color}-500/10 flex items-center justify-center text-${activeQuadrantObj.color}-400`}>
                                                <TrendingUp size={16} />
                                             </div>
                                             <div className="text-lg font-black text-white">{activeFirmObj.targetMultiple} Target</div>
                                          </div>
                                          <p className="text-sm font-medium text-slate-300 leading-relaxed border-l-2 border-emerald-500/50 pl-4">
                                             {activeFirmObj.pivotPath}
                                          </p>
                                       </div>
                                    ) : (
                                       <div className="text-sm font-medium text-slate-400 italic">
                                          Aggressive Market Capture. Consolidate L2 infrastructure to underwrite L3 reasoning logic.
                                       </div>
                                    )}
                                 </div>

                                 <div className="bg-slate-950 border border-slate-800 rounded-3xl p-6">
                                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-4">Alpha Lock-In Methodology</div>
                                    <div className="space-y-4">
                                       <div className="flex items-center gap-3">
                                          <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                                          <span className="text-[11px] font-bold text-slate-300 uppercase tracking-widest">Substrate Rooting</span>
                                       </div>
                                       <div className="flex items-center gap-3">
                                          <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                                          <span className="text-[11px] font-bold text-slate-300 uppercase tracking-widest">Telemetry Reciprocity</span>
                                       </div>
                                       <div className="flex items-center gap-3 opacity-50">
                                          <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                                          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Terminal Authority</span>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           )}
                        </div>
                     </div>
                  </div>
               </div>
            ) : (
               // Default Display (Original Playbook Overview)
               quadrantTactics.map((q, i) => (
                  <div 
                    key={i} 
                    onClick={() => setSelectedQuadrant(q.id)}
                    className={`bg-slate-900/40 border border-slate-800 rounded-2xl p-6 hover:border-${q.color}-500/30 cursor-pointer transition-colors relative overflow-hidden group shadow-lg`}
                  >
                     <div className={`absolute top-0 right-0 w-32 h-32 bg-${q.color}-500/5 rounded-full blur-[40px] pointer-events-none group-hover:bg-${q.color}-500/10 transition-colors`}></div>
                     
                     <div className={`text-[11px] font-black uppercase tracking-widest text-${q.color}-400 mb-1`}>{q.name}</div>
                     <div className="text-[8px] font-bold uppercase tracking-widest text-slate-500 mb-5">{q.position}</div>
                     
                     <div className="mb-5">
                        <div className="text-[8px] font-black uppercase tracking-widest text-slate-600 mb-2 flex items-center gap-1.5"><Info size={10}/> Implication</div>
                        <div className="text-[11px] text-slate-300 leading-relaxed font-medium">{q.implication}</div>
                     </div>
                     
                     <div className="pt-5 border-t border-slate-800 mt-auto">
                        <div className="text-[8px] font-black uppercase tracking-widest text-slate-600 mb-2 flex items-center gap-1.5"><Target size={10}/> Tactical Play</div>
                        <div className="text-[11px] font-bold text-white leading-relaxed">{q.tacticalPlay}</div>
                     </div>
                  </div>
               ))
            )}
         </div>

         {/* Collapsible Ledger Section */}
         <div className="mt-8">
            <button 
              onClick={() => setIsLedgerExpanded(!isLedgerExpanded)}
              className={`w-full py-5 flex items-center justify-center gap-3 border bg-slate-900/40 hover:bg-slate-800 text-xs font-black uppercase tracking-[0.2em] rounded-[24px] transition-all shadow-sm ${isLedgerExpanded ? 'border-indigo-500/50 text-indigo-300' : 'border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white'}`}
            >
              <Database size={16} className={isLedgerExpanded ? "text-indigo-400 animate-pulse" : ""} />
              {isLedgerExpanded ? 'HIDE UNDERWRITING LEDGER' : 'VIEW FULL UNDERWRITING LEDGER'}
              {isLedgerExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            
            {isLedgerExpanded && (
               <div className="mt-6 animate-in slide-in-from-top-4 fade-in duration-500">
                 <ScoringLedgerView />
               </div>
            )}
         </div>
      </div>
    </div>
  );
};

const ScoringLedgerView = () => {
  const [filter, setFilter] = useState('All');
  const [showDefinitions, setShowDefinitions] = useState(false);
  const filteredData = filter === 'All' ? PortfolioData : PortfolioData.filter(f => f.quadrant === filter);

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
       <div className="bg-[#030712] border border-slate-800 rounded-[32px] overflow-hidden shadow-2xl">
          <div className="p-8 border-b border-slate-800 bg-slate-900/40 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
             <div>
                <h3 className="text-2xl font-black text-white mb-2 tracking-tight">Sovereign Underwriting Ledger</h3>
                <p className="text-slate-400 text-sm max-w-2xl">Detailed scoring breakdown and strategic rationale for all portfolio assets. Unpacking the logic behind the multi-agent verdicts.</p>
             </div>
             
             <div className="flex flex-col gap-2 shrink-0">
                <div className="flex items-center justify-between gap-4">
                   <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                      <Filter size={10} /> Filter by Verdict Quadrant
                   </div>
                   <button 
                     onClick={() => setShowDefinitions(!showDefinitions)}
                     className={`flex items-center gap-1.5 px-2 py-1 rounded text-[8px] font-black uppercase tracking-widest transition-all border ${showDefinitions ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' : 'bg-slate-900 text-slate-500 border-slate-700 hover:text-slate-300'}`}
                   >
                     <Info size={10} /> FSMM Key
                   </button>
                </div>
                <div className="flex items-center gap-1 bg-slate-950 border border-slate-800 rounded-lg p-1">
                   {['All', 'Titans', 'Spines', 'Harvest', 'Dead Zone'].map(q => (
                     <button 
                       key={q}
                       onClick={() => setFilter(q)}
                       className={`px-3 py-1.5 rounded text-[9px] font-black uppercase tracking-widest transition-all ${
                         filter === q 
                          ? (q === 'Titans' ? 'bg-emerald-500/20 text-emerald-400' : 
                             q === 'Spines' ? 'bg-purple-500/20 text-purple-400' :
                             q === 'Harvest' ? 'bg-amber-500/20 text-amber-400' :
                             q === 'Dead Zone' ? 'bg-rose-500/20 text-rose-400' : 'bg-slate-700 text-white')
                          : 'text-slate-500 hover:text-slate-300'
                       }`}
                     >
                       {q}
                     </button>
                   ))}
                </div>
             </div>
          </div>

          {showDefinitions && (
            <div className="bg-slate-900/80 border-b border-slate-800 p-6 md:p-8 animate-in fade-in slide-in-from-top-2 duration-300">
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                  <div>
                     <div className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-4 border-b border-slate-800 pb-2 flex items-center gap-2">
                       <Shield size={12} /> Moat Factors (M<sub>s</sub>) "The Defense"
                     </div>
                     <div className="space-y-4">
                        <div>
                          <div className="text-[10px] font-bold text-white flex justify-between"><span>P-Foundation</span> <span className="text-slate-500">Weight: 30%</span></div>
                          <div className="text-[10px] text-slate-400 leading-relaxed mt-1">Physical Origin node ownership. Does the firm own proprietary hardware, spectrum, or unique asset telemetry that cannot be scraped by an AI model?</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold text-white flex justify-between"><span>H-Accretion</span> <span className="text-slate-500">Weight: 30%</span></div>
                          <div className="text-[10px] text-slate-400 leading-relaxed mt-1">Heuristic depth. Does the model use proprietary, specialized logic or algorithms that are not found in public LLM training data?</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold text-white flex justify-between"><span>W-Sovereignty</span> <span className="text-slate-500">Weight: 40%</span></div>
                          <div className="text-[10px] text-slate-400 leading-relaxed mt-1">Workflow embedding. Is it the terminal "Industry Spine" for a regulated process? How painful is the "rip-and-replace" process?</div>
                        </div>
                     </div>
                  </div>

                  <div>
                     <div className="text-[10px] font-black text-rose-400 uppercase tracking-widest mb-4 border-b border-slate-800 pb-2 flex items-center gap-2">
                       <AlertTriangle size={12} /> Risk Factors (R<sub>s</sub>) "The Disruption"
                     </div>
                     <div className="space-y-4 mb-5">
                        <div>
                          <div className="text-[10px] font-bold text-white flex justify-between"><span>L-Commoditization</span> <span className="text-slate-500">Weight: 50%</span></div>
                          <div className="text-[10px] text-slate-400 leading-relaxed mt-1">Logical Risk. Can a general-purpose model (Gemini/Claude) reason through and replicate the core product's output today or within 12 months?</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold text-white flex justify-between"><span>L-Scaling</span> <span className="text-slate-500">Weight: 30%</span></div>
                          <div className="text-[10px] text-slate-400 leading-relaxed mt-1">Labor Density. Does scaling the product require a linear increase in human experts/services, or does it scale exponentially like pure software?</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold text-white flex justify-between"><span>O-Dependency</span> <span className="text-slate-500">Weight: 20%</span></div>
                          <div className="text-[10px] text-slate-400 leading-relaxed mt-1">Platform Risk. Is the asset built on someone else's infrastructure, APIs, or data models, making it highly vulnerable to upstream architectural changes?</div>
                        </div>
                     </div>
                     <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-[9px] text-slate-400 italic">
                        <span className="font-bold text-emerald-400">Note on Matrix Plotting:</span> The Sovereign Matrix inverts Risk (R<sub>s</sub>) into <strong className="text-white">Disruption Resistance (6 - R<sub>s</sub>)</strong> so that highly defensible assets are plotted correctly in the Top-Right "Winner's Circle".
                     </div>
                  </div>
               </div>
            </div>
          )}

          <div className="overflow-x-auto max-h-[800px] custom-scrollbar">
             <table className="w-full text-left border-collapse min-w-[1000px]">
                <thead className="sticky top-0 z-20 shadow-md">
                   <tr className="bg-slate-950 border-b border-slate-800 text-[9px] font-black uppercase tracking-widest text-slate-500">
                      <th className="p-6 w-1/5">Asset / Sector</th>
                      <th className="p-6 w-1/4">Moat Factors (M<sub>s</sub>)</th>
                      <th className="p-6 w-1/4">Risk Factors (R<sub>s</sub>)</th>
                      <th className="p-6 w-1/4">Strategic Rationale</th>
                   </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                   {filteredData.map((firm, i) => (
                      <tr key={i} className="hover:bg-slate-900/20 transition-colors group">
                         <td className="p-6 align-top">
                            <div className="text-base font-bold text-white mb-1 flex items-center gap-2">
                              <FirmLogo domain={firm.domain} company={firm.company} className="w-5 h-5 rounded-[4px]" />
                              {firm.company}
                            </div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">{firm.sector}</div>
                            <div className={`inline-flex px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest mb-3 ${
                              firm.quadrant === 'Titans' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 
                              firm.quadrant === 'Spines' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 
                              firm.quadrant === 'Harvest' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                              'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                            }`}>
                               {firm.quadrant}
                            </div>
                            <div className="flex flex-col gap-2">
                               <div className="text-[10px] font-black text-indigo-400 bg-indigo-500/10 px-2 py-1 rounded w-fit">M<sub>s</sub>: {firm.ms}</div>
                               <div className="text-[10px] font-black text-rose-400 bg-rose-500/10 px-2 py-1 rounded w-fit">R<sub>s</sub>: {firm.rs}</div>
                            </div>
                         </td>
                         
                         <td className="p-6 align-top">
                            <div className="space-y-4">
                               {firm.moatBreakdown.map((m, idx) => (
                                  <div key={idx}>
                                     <div className="flex justify-between items-center mb-1">
                                        <span className="text-[9px] font-black text-indigo-300 uppercase tracking-widest">{m.factor}</span>
                                        <span className="text-[10px] font-bold text-white bg-slate-800 px-1.5 py-0.5 rounded">{m.score}/5</span>
                                     </div>
                                     <p className="text-[11px] text-slate-400 leading-snug">{m.rationale}</p>
                                  </div>
                               ))}
                            </div>
                         </td>

                         <td className="p-6 align-top">
                            <div className="space-y-4">
                               {firm.riskBreakdown.map((r, idx) => (
                                  <div key={idx}>
                                     <div className="flex justify-between items-center mb-1">
                                        <span className="text-[9px] font-black text-rose-300 uppercase tracking-widest">{r.factor}</span>
                                        <span className="text-[10px] font-bold text-white bg-slate-800 px-1.5 py-0.5 rounded">{r.score}/5</span>
                                     </div>
                                     <p className="text-[11px] text-slate-400 leading-snug">{r.rationale}</p>
                                  </div>
                               ))}
                            </div>
                         </td>

                         <td className="p-6 align-top">
                            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 h-full shadow-inner flex flex-col">
                               <div className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-2 flex items-center justify-between">
                                  <span className="flex items-center gap-1.5"><Cpu size={10} className="text-indigo-400" /> Frontier Verdict</span>
                                  <span className="text-[6px] text-slate-600 tracking-wider">MULTI-AGENT CHAIN</span>
                               </div>
                               <div className="text-[11px] text-slate-300 font-medium italic leading-relaxed">
                                  "{firm.verdict}"
                               </div>
                            </div>
                         </td>
                      </tr>
                   ))}
                </tbody>
             </table>
          </div>
       </div>
    </div>
  );
};

const SectorBrainsVault = () => {
  const [hoveredSector, setHoveredSector] = useState(null);
  const [zoomedSectorData, setZoomedSectorData] = useState(null);
  const [viewLens, setViewLens] = useState('standard'); 
  const [traceActive, setTraceActive] = useState(false);
  const [expandedLayers, setExpandedLayers] = useState({});
  const [isDistExpanded, setIsDistExpanded] = useState(false);
  const toggleLayer = (layerId) => setExpandedLayers(prev => ({ ...prev, [layerId]: !prev[layerId] }));

  const stackData = [
    {
      id: 'intent',
      title: 'Autonomous Intent',
      subtitle: 'The Commerce Layer',
      role: 'L4 • Outcome',
      color: 'purple',
      icon: Target,
      pivot: 'Outcome-Based Settlement (ARR)',
      sectors: [
        { name: 'Retail', active: true, icon: ShoppingBag, sovereignty: 'S', debt: 'High', pivot: '12x', wrapper: false, dependsOn: ['Logistics', 'Telecom', 'Compute'], firms: [{name: 'Convertcart', color: 'amber'}, {name: 'Mason', color: 'purple'}] },
        { name: 'Real Estate', active: false, icon: Home, sovereignty: 'S', debt: 'Med', pivot: '18x', wrapper: false, dependsOn: ['Energy & Utils', 'Cyber'] },
        { name: 'Hospitality', active: false, icon: Globe, sovereignty: 'W', debt: 'High', pivot: '9x', wrapper: true, dependsOn: ['Logistics', 'Cloud'] },
        { name: 'Media', active: false, icon: PlayCircle, sovereignty: 'S', debt: 'Low', pivot: '14x', wrapper: false, dependsOn: ['Telecom', 'Compute'] },
        { name: 'Mobility', active: true, icon: Navigation, sovereignty: 'S', debt: 'Med', pivot: '16x', wrapper: false, dependsOn: ['Logistics', 'Energy & Utils'], firms: [{name: 'FlyNava Technologies', color: 'indigo'}] },
        { name: 'Enterprise Tech', active: true, icon: Briefcase, sovereignty: 'W', debt: 'High', pivot: '9x', wrapper: true, dependsOn: ['Cloud'], firms: [{name: 'B2Brain', color: 'purple'}, {name: 'Zapty', color: 'rose'}] },
        { name: 'Ad-Tech', active: true, icon: Target, sovereignty: 'S', debt: 'Med', pivot: '15x', wrapper: false, dependsOn: ['Cyber', 'Cloud', 'Telecom'], firms: [{name: 'ClearTrust', color: 'cyan'}] }
      ]
    },
    {
      id: 'cognitive',
      title: 'Cognitive Foundations',
      subtitle: 'The Intelligence Layer',
      role: 'L3 • Logic',
      color: 'cyan',
      icon: Cpu,
      pivot: 'Labor Billing → Reasoning ARR',
      sectors: [
        { name: 'HCLS', active: true, icon: Search, sovereignty: 'S', debt: 'High', pivot: '18x', wrapper: false, dependsOn: ['Cyber', 'Compute', 'Logistics'], firms: [{name: 'ManageArtworks', color: 'purple'}, {name: 'K-Cat', color: 'cyan'}, {name: 'Ahammune', color: 'emerald'}, {name: 'Litemed', color: 'cyan'}] },
        { name: 'IT Services', active: true, icon: Terminal, sovereignty: 'W', debt: 'Extreme', pivot: '14x', wrapper: true, dependsOn: ['Cloud', 'Cyber', 'Telecom'], firms: [{name: 'LatentForce', color: 'indigo'}, {name: 'Mihup', color: 'indigo'}, {name: 'Peritus.ai', color: 'blue'}] },
        { name: 'Fintech', active: true, icon: Home, sovereignty: 'S', debt: 'Med', pivot: '22x', wrapper: false, dependsOn: ['Cyber', 'Compute', 'Telecom'], firms: [{name: 'Growthpal', color: 'blue'}] },
        { name: 'Legal', active: false, icon: Briefcase, sovereignty: 'W', debt: 'Extreme', pivot: '12x', wrapper: true, dependsOn: ['Cyber', 'Cloud'] },
        { name: 'GovTech', active: false, icon: Shield, sovereignty: 'S', debt: 'Med', pivot: '15x', wrapper: false, dependsOn: ['Defense', 'Cyber', 'Telecom'] },
        { name: 'EdTech', active: true, icon: Book, sovereignty: 'W', debt: 'High', pivot: '10x', wrapper: true, dependsOn: ['Cloud', 'Compute'], firms: [{name: 'Gooru', color: 'rose'}] },
        { name: 'Prof Services', active: true, icon: Briefcase, sovereignty: 'W', debt: 'Extreme', pivot: '12x', wrapper: true, dependsOn: ['Cloud', 'Cyber'], firms: [{name: 'Worxogo', color: 'indigo'}, {name: 'Bewgle', color: 'rose'}] },
        { name: 'Spatial Tech', active: true, icon: Navigation, sovereignty: 'S', debt: 'Med', pivot: '18x', wrapper: false, dependsOn: ['Compute', 'Telecom'], firms: [{name: 'Whodat', color: 'amber'}] }
      ]
    },
    {
      id: 'infrastructure',
      title: 'Digital Infrastructure',
      subtitle: 'The Flow Layer',
      role: 'L2 • Network',
      color: 'indigo',
      icon: Network,
      pivot: 'Commodity Utility → Intel Routing',
      sectors: [
        { name: 'Telecom', active: true, icon: Radio, sovereignty: 'S', debt: 'Low', pivot: '10x', wrapper: false, dependsOn: ['Energy & Utils', 'Space', 'Semis'], firms: [{name: 'Lavelle Networks', color: 'indigo'}] },
        { name: 'Cyber', active: true, icon: Lock, sovereignty: 'S', debt: 'Med', pivot: '15x', wrapper: false, dependsOn: ['Defense', 'Compute'], firms: [{name: 'InfiSecure', color: 'purple'}, {name: 'Saigeware', color: 'indigo'}] },
        { name: 'Compute', active: true, icon: Server, sovereignty: 'S', debt: 'Low', pivot: '18x', wrapper: false, dependsOn: ['Energy & Utils', 'Semis'], firms: [{name: 'Accelequant', color: 'cyan'}, {name: 'Gyrophint', color: 'blue'}] },
        { name: 'Cloud', active: true, icon: Cloud, sovereignty: 'W', debt: 'Low', pivot: '12x', wrapper: true, dependsOn: ['Compute', 'Telecom'], firms: [{name: 'Nimesa', color: 'indigo'}, {name: 'Spanugo', color: 'blue'}] },
        { name: 'Environmental', active: true, icon: Leaf, sovereignty: 'S', debt: 'Low', pivot: '14x', wrapper: false, dependsOn: ['Space', 'Compute'], firms: [{name: 'Ambee', color: 'emerald'}] },
        { name: 'Space', active: true, icon: Radio, sovereignty: 'S', debt: 'Low', pivot: '30x', wrapper: false, dependsOn: ['Defense', 'Semis'], firms: [{name: 'Okulo Aerospace', color: 'emerald'}] }
      ]
    },
    {
      id: 'resilience',
      title: 'Sovereign Resilience',
      subtitle: 'The Physical Spine',
      role: 'L1 • Asset',
      color: 'emerald',
      icon: Shield,
      pivot: 'Maintenance Cost → Origin Moat',
      sectors: [
        { name: 'Defense', active: true, icon: Target, sovereignty: 'S', debt: 'Med', pivot: '12x', wrapper: false, dependsOn: [], firms: [{name: 'BluJ Aero', color: 'emerald'}] },
        { 
          name: 'Logistics', 
          active: true, 
          icon: Truck,
          hasDeepDive: true,
          sovereignty: 'S', debt: 'Extreme', pivot: '18x', wrapper: false,
          dependsOn: [],
          firms: [{name: 'SmartShip', color: 'emerald'}],
          deepDive: {
            themeColor: 'cyan',
            fullName: 'Freight & Logistics',
            custodian: {
              name: 'Thomas Kipp',
              role: 'Logistics AI & Global Supply',
              img: "\images\profile_Thomas.jpg"
            },
            slices: [
              { name: 'Maritime & Port Infrastructure', layer: 'Asset-Heavy', desc: 'The Global Arteries. Managing massive-scale containerization, bulk flows, and the critical land-sea interfaces.', focus: 'Container Lines, Dry Bulk, Automated Terminals, Stevedoring', icon: Anchor },
              { name: 'Air Freight & Integrators', layer: 'Asset-Heavy', desc: "The Velocity Layer. High-value, low-weight cargo and the global 'door-to-door' integrators who control their own planes.", focus: 'Air Cargo Carriers, Express Integrators, Airport Ground Handling', icon: Navigation },
              { name: 'Surface Transport (TL & LTL)', layer: 'Asset-Right', desc: 'The Economic Pulse. The backbone of domestic trade, spanning long-haul \'Truckload\' and complex hub-and-spoke networks.', focus: 'Dry Van, Refrigerated, LTL Carrier Networks, Specialized Flatbed', icon: Truck },
              { name: 'Rail & Intermodal', layer: 'Asset-Heavy', desc: 'The High-Efficiency Spine. Moving massive volumes overland with the lowest carbon footprint per ton-mile.', focus: 'Class I Railroads, Short-Line Operators, Intermodal Ramps', icon: Truck },
              { name: 'Freight Forwarding', layer: 'Asset-Light', desc: 'The Trade Architects. The intellectual layer that buys wholesale capacity and manages door-to-port-to-door complexity.', focus: 'Ocean/Air Forwarding, Customs Brokerage, Trade Compliance', icon: Globe },
              { name: 'Contract Logistics & 3PL/4PL', layer: 'Asset-Light', desc: 'The Outsourced Brain. Deeply integrated partners managing warehousing, inventory, and end-to-end supply chain strategy.', focus: 'Warehouse Mgmt, Multi-Client Fulfillment, Lead Logistics Providers', icon: Box },
              { name: 'Last-Mile & E-commerce', layer: 'Asset-Right', desc: 'The Friction Point. The most expensive and visible leg of the journey, solving for consumer density and speed.', focus: 'Parcel Delivery, Urban Micro-Hubs, PUDO Networks, Drones/Bots', icon: MapPin },
              { name: 'Specialized & Cold Chain', layer: 'Functional Moat', desc: 'High-Fidelity Chains. Solving for extreme constraints: temperature (Pharma/Food) or dimension (Project Cargo).', focus: 'Bio-Pharma Cold Chain, Perishable Food, Heavy-Lift, Project Cargo', icon: Cloud },
              { name: 'Digital Freight & LogTech', layer: 'Systemic Layer', desc: 'The Operating System. Software-led disruptors providing the visibility, matching, and data layer for the entire ecosystem.', focus: 'Digital Freight Brokerage, TMS/WMS SaaS, Visibility Platforms, AI Dispatch', icon: Cpu }
            ]
          }
        },
        { name: 'Energy & Utils', active: true, icon: Zap, sovereignty: 'S', debt: 'Extreme', pivot: '15x', wrapper: false, dependsOn: [], firms: [{name: 'Numocity', color: 'amber'}, {name: 'Phyx44', color: 'amber'}] },
        { name: 'Mfg', active: true, icon: Factory, sovereignty: 'S', debt: 'High', pivot: '12x', wrapper: false, dependsOn: [], firms: [{name: 'Simyog', color: 'orange'}] },
        { name: 'Semis', active: true, icon: Cpu, sovereignty: 'S', debt: 'Low', pivot: '25x', wrapper: false, dependsOn: ['Resources'], firms: [{name: 'HrdWyr', color: 'emerald'}] },
        { name: 'AgTech', active: false, icon: Leaf, sovereignty: 'S', debt: 'Med', pivot: '10x', wrapper: false, dependsOn: [] },
        { name: 'Resources', active: false, icon: PenTool, sovereignty: 'S', debt: 'Med', pivot: '8x', wrapper: false, dependsOn: [] },
        { name: 'Construction', active: false, icon: Settings, sovereignty: 'S', debt: 'Extreme', pivot: '12x', wrapper: false, dependsOn: ['Mfg', 'Logistics'] }
      ]
    }
  ];

  const getActiveDependencies = () => {
    if (!hoveredSector) return [];
    const [layerId, sIdx] = hoveredSector.split('-');
    const layer = stackData.find(l => l.id === layerId);
    if (!layer) return [];
    const startSector = layer.sectors[sIdx];
    const deps = new Set();
    const trace = (sectorName) => {
      for (const l of stackData) {
        const s = l.sectors.find(x => x.name === sectorName);
        if (s && s.dependsOn) {
          s.dependsOn.forEach(d => {
            if (!deps.has(d)) {
              deps.add(d);
              trace(d);
            }
          });
          break;
        }
      }
    };
    trace(startSector.name);
    return Array.from(deps);
  };

  const activeDependencies = traceActive ? getActiveDependencies() : [];
  const isHoveringWithDeps = traceActive && hoveredSector && activeDependencies.length > 0;

  const activeSectors = stackData.reduce((acc, layer) => acc + layer.sectors.filter(s => s.active).length, 0);
  const totalSectors = stackData.reduce((acc, layer) => acc + layer.sectors.length, 0);
  const mappedFirms = stackData.reduce((acc, layer) => acc + layer.sectors.reduce((fAcc, s) => fAcc + (s.firms ? s.firms.length : 0), 0), 0);

  const getLayerFirmCount = (layerId) => {
    const layer = stackData.find(l => l.id === layerId);
    return layer ? layer.sectors.reduce((acc, s) => acc + (s.firms ? s.firms.length : 0), 0) : 0;
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 flex flex-col justify-center relative w-full mb-8">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[radial-gradient(#334155_1px,transparent_1px)] [background-size:24px_24px]"></div>

      <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 pointer-events-none ${isHoveringWithDeps ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}>
         <div className="bg-slate-900/95 border border-emerald-500/50 px-6 py-4 rounded-full flex items-center gap-3 backdrop-blur-xl shadow-[0_0_40px_rgba(16,185,129,0.3)]">
            <Network size={16} className="text-emerald-400 animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">Sector Physics Active:</span>
            <span className="text-[10px] font-bold text-slate-300">Alpha structurally rolls up from glowing L1/L2 nodes.</span>
         </div>
      </div>

      <div className="max-w-7xl mx-auto w-full relative z-10 transition-all duration-500" style={{ filter: zoomedSectorData ? 'blur(10px)' : 'none' }}>
        
        <MandateHeader
          title="MACRO-THESIS ALIGNMENT"
          metric={`${mappedFirms} ASSETS LOGGED`}
          pulseLabel="THESIS DRIFT"
          pulseText="Over-indexed in L3 Logic. Substrate hedge required."
          pulseColor="rose"
        />

        {/* 1. Structural Distribution Panel (Moved Up) */}
        <div className="mb-8 bg-slate-900/30 border border-slate-800/60 rounded-2xl shadow-lg backdrop-blur-md animate-in fade-in duration-500 relative overflow-hidden transition-all">
           <div className="absolute top-0 right-0 w-64 h-full bg-gradient-to-l from-indigo-500/5 to-transparent pointer-events-none"></div>
           
           <button 
             onClick={() => setIsDistExpanded(!isDistExpanded)}
             className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-5 relative z-10 hover:bg-slate-800/30 transition-colors cursor-pointer"
           >
              <div className="flex items-center gap-5">
                 <div className="flex items-center gap-2">
                    <Activity size={14} className="text-indigo-400" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Active Sectors: <strong className="text-white ml-1 text-xs">{activeSectors}</strong><span className="text-slate-600">/{totalSectors}</span></span>
                 </div>
                 <div className="w-px h-4 bg-slate-800 hidden sm:block"></div>
                 <div className="flex items-center gap-2">
                    <Target size={14} className="text-emerald-400" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Mapped Assets: <strong className="text-white ml-1 text-xs">{mappedFirms}</strong><span className="text-slate-600">/{PortfolioData.length}</span></span>
                 </div>
              </div>
              <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 bg-slate-950 px-2.5 py-1 rounded-md border border-slate-800 shadow-inner">
                     Structural Distribution
                  </div>
                  {isDistExpanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
              </div>
           </button>

           {isDistExpanded && (
             <div className="px-5 pb-5 pt-2 relative z-10 animate-in slide-in-from-top-2 duration-300 border-t border-slate-800/50 mt-2">
               {/* The Distribution Bar */}
               <div className="w-full h-2.5 bg-slate-950 rounded-full overflow-hidden flex shadow-inner border border-slate-800/80 mb-4 relative z-10">
                  {mappedFirms > 0 && (
                    <>
                      <div style={{ width: `${(getLayerFirmCount('intent')/mappedFirms)*100}%` }} className="h-full bg-purple-500 hover:brightness-125 transition-all"></div>
                      <div style={{ width: `${(getLayerFirmCount('cognitive')/mappedFirms)*100}%` }} className="h-full bg-cyan-500 border-l border-slate-950 hover:brightness-125 transition-all"></div>
                      <div style={{ width: `${(getLayerFirmCount('infrastructure')/mappedFirms)*100}%` }} className="h-full bg-indigo-500 border-l border-slate-950 hover:brightness-125 transition-all"></div>
                      <div style={{ width: `${(getLayerFirmCount('resilience')/mappedFirms)*100}%` }} className="h-full bg-emerald-500 border-l border-slate-950 hover:brightness-125 transition-all"></div>
                    </>
                  )}
               </div>

               {/* Legend */}
               <div className="flex justify-between items-start relative z-10 w-full">
                  <div className="flex flex-col gap-1 w-1/4 pr-2">
                     <div className="flex items-center gap-1.5 text-[8px] font-black text-purple-400 uppercase tracking-widest"><div className="w-1.5 h-1.5 rounded-full bg-purple-500 shadow-[0_0_8px_currentColor]"></div> L4 Outcome</div>
                     <div className="text-sm font-bold text-white leading-none">{getLayerFirmCount('intent')} <span className="text-slate-600 text-[9px] font-medium uppercase tracking-wider">Assets</span></div>
                  </div>
                  <div className="flex flex-col gap-1 w-1/4 text-left border-l border-slate-800 pl-4 pr-2">
                     <div className="flex items-center gap-1.5 text-[8px] font-black text-cyan-400 uppercase tracking-widest"><div className="w-1.5 h-1.5 rounded-full bg-cyan-500 shadow-[0_0_8px_currentColor]"></div> L3 Logic</div>
                     <div className="text-sm font-bold text-white leading-none">{getLayerFirmCount('cognitive')} <span className="text-slate-600 text-[9px] font-medium uppercase tracking-wider">Assets</span></div>
                  </div>
                  <div className="flex flex-col gap-1 w-1/4 text-left border-l border-slate-800 pl-4 pr-2">
                     <div className="flex items-center gap-1.5 text-[8px] font-black text-indigo-400 uppercase tracking-widest"><div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shadow-[0_0_8px_currentColor]"></div> L2 Network</div>
                     <div className="text-sm font-bold text-white leading-none">{getLayerFirmCount('infrastructure')} <span className="text-slate-600 text-[9px] font-medium uppercase tracking-wider">Assets</span></div>
                  </div>
                  <div className="flex flex-col gap-1 w-1/4 text-left border-l border-slate-800 pl-4">
                     <div className="flex items-center gap-1.5 text-[8px] font-black text-emerald-400 uppercase tracking-widest"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_currentColor]"></div> L1 Asset</div>
                     <div className="text-sm font-bold text-white leading-none">{getLayerFirmCount('resilience')} <span className="text-slate-600 text-[9px] font-medium uppercase tracking-wider">Assets</span></div>
                  </div>
               </div>
             </div>
           )}
        </div>

        {/* 2. Control Panels (Right Aligned) */}
        <div className="mb-8 flex flex-col xl:flex-row xl:items-center justify-between gap-4 animate-in fade-in duration-500">
          
          <div className="w-full xl:w-auto flex items-center order-2 xl:order-1 overflow-hidden">
            {viewLens === 'risk' && (
              <div className="flex flex-wrap items-center gap-3 sm:gap-5 bg-slate-900/40 border border-slate-800/60 px-4 py-2.5 rounded-xl backdrop-blur-sm shadow-sm">
                 <div className="flex items-center gap-2">
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Manual Labor Debt</span>
                    <div className="flex gap-1">
                       <div className="w-3 h-1.5 bg-emerald-500 rounded-full"></div>
                       <div className="w-3 h-1.5 bg-orange-500 rounded-full"></div>
                       <div className="w-3 h-1.5 bg-rose-500 rounded-full"></div>
                    </div>
                 </div>
                 <div className="h-4 w-px bg-slate-800 hidden sm:block"></div>
                 <div className="flex items-center gap-1.5">
                    <div className="bg-rose-500/20 p-1 rounded border border-rose-500/40"><AlertTriangle size={10} className="text-rose-400" /></div>
                    <span className="text-[8px] font-bold text-rose-300 uppercase tracking-widest truncate">Wrapper Risk: Models Rented, Not Owned</span>
                 </div>
              </div>
            )}

            {viewLens === 'valuation' && (
              <div className="flex flex-wrap items-center gap-3 sm:gap-5 bg-slate-900/40 border border-slate-800/60 px-4 py-2.5 rounded-xl backdrop-blur-sm shadow-sm">
                 <div className="flex items-center gap-2">
                    <span className="text-[8px] font-black px-1.5 rounded border text-emerald-400 border-emerald-500/40 bg-emerald-500/20 uppercase">S</span>
                    <span className="text-[8px] font-bold text-slate-300 uppercase tracking-widest">Sovereign Moat <span className="text-[7px] text-slate-500 ml-1 hidden sm:inline">(Origin Rooted)</span></span>
                 </div>
                 <div className="h-4 w-px bg-slate-800 hidden sm:block"></div>
                 <div className="flex items-center gap-2">
                    <span className="text-[8px] font-black px-1.5 rounded border text-rose-400 border-rose-500/40 bg-rose-500/20 uppercase">W</span>
                    <span className="text-[8px] font-bold text-slate-300 uppercase tracking-widest">Wrapper Risk <span className="text-[7px] text-slate-500 ml-1 hidden sm:inline">(Dependent)</span></span>
                 </div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 bg-[#0a0f1e]/80 border border-slate-800 rounded-2xl p-2 backdrop-blur-md shadow-xl w-full xl:w-auto shrink-0 order-1 xl:order-2">
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-1 flex items-center gap-1 w-full sm:w-auto">
              {[
                { id: 'standard', label: 'Standard', icon: Globe },
                { id: 'risk', label: 'Risk Audit', icon: AlertTriangle },
                { id: 'valuation', label: 'Valuation', icon: TrendingUp }
              ].map((lens) => (
                <button
                  key={lens.id}
                  onClick={() => setViewLens(lens.id)}
                  className={`flex-1 sm:flex-none flex justify-center items-center gap-1.5 px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all duration-300 ${
                    viewLens === lens.id 
                      ? 'bg-indigo-600 text-white shadow-md' 
                      : 'text-slate-500 hover:text-slate-200 hover:bg-slate-800'
                  }`}
                >
                  <lens.icon size={12} />
                  <span>{lens.label}</span>
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setTraceActive(!traceActive)}
              className={`flex justify-center items-center gap-2 px-4 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all duration-300 w-full sm:w-auto ${
                traceActive 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.2)]' 
                  : 'bg-slate-900 border border-slate-800 text-slate-500 hover:text-slate-300 hover:bg-slate-800'
              }`}
            >
              <Network size={12} className={traceActive ? "animate-pulse" : ""} />
              <span>{traceActive ? 'Sector Physics: Active' : 'Activate Sector Physics'}</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          {stackData.map((layer) => {
            const hasDependencyInLayer = layer.sectors.some(s => activeDependencies.includes(s.name));
            const isHoveredLayer = hoveredSector && hoveredSector.startsWith(layer.id);
            const inactiveCount = layer.sectors.filter(s => !s.active).length;
            const isExpanded = expandedLayers[layer.id];
            
            // Dynamic High-Alpha Target Logic
            const isHighAlphaTarget = (s) => !s.active && !s.wrapper && parseInt(s.pivot) >= 15;
            const targetCount = layer.sectors.filter(isHighAlphaTarget).length;
            const isTargetLayer = viewLens === 'valuation' && targetCount > 0;

            return (
              <div key={layer.id} className="group relative flex flex-col md:flex-row gap-2">
                <div className="hidden md:flex flex-col items-center w-8 pt-4 relative">
                   <div className={`w-1 h-full rounded-full transition-all duration-700 
                      ${hasDependencyInLayer ? 'bg-emerald-500 shadow-[0_0_20px_#10b981]' : 
                        isHoveredLayer ? `bg-${layer.color}-500 shadow-[0_0_15px_rgba(var(--${layer.color}-500-rgb),0.5)]` : 
                        `bg-gradient-to-b from-${layer.color}-500/30 to-transparent shadow-[0_0_10px_rgba(var(--${layer.color}-500-rgb),0.1)] group-hover:from-${layer.color}-500/60`
                      }`}
                   ></div>
                </div>

                <div className={`flex-1 bg-[#050a18]/90 border rounded-[32px] p-5 flex flex-col md:flex-row items-center gap-4 transition-all duration-700 backdrop-blur-sm
                   ${hasDependencyInLayer ? 'border-emerald-500/30 bg-[#06101c]' : 'border-slate-800/80 group-hover:border-slate-700/80 group-hover:bg-[#070e24] shadow-xl'}
                `}>
                  <div className="w-full md:w-56 shrink-0 flex items-center gap-4 border-b md:border-b-0 md:border-r border-slate-800/50 pb-3 md:pb-0 md:pr-4">
                     <div className={`p-2.5 rounded-xl border transition-colors duration-500 ${hasDependencyInLayer ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400' : `bg-slate-900 border-slate-800 text-${layer.color}-500 shadow-inner`}`}>
                        <layer.icon size={20} />
                     </div>
                     <div className="flex flex-col">
                        <div className={`text-[8px] font-black uppercase tracking-[0.2em] mb-0.5 transition-colors ${hasDependencyInLayer ? 'text-emerald-500/80' : `text-${layer.color}-500/80`}`}>
                          {layer.role}
                        </div>
                        <h3 className="text-sm font-black text-white leading-tight tracking-tight uppercase">
                          {layer.title}
                        </h3>
                        <div className="text-[9px] font-medium text-slate-400 italic">
                          {layer.subtitle}
                        </div>
                     </div>
                  </div>

                  <div className="flex-1 w-full py-1">
                     <div className="flex flex-wrap items-stretch gap-3">
                        {layer.sectors.map((sector, sIdx) => {
                          if (!sector.active && !isExpanded) return null;
                          const isHoveredBlock = hoveredSector === `${layer.id}-${sIdx}`;
                          const isDependencyBlock = activeDependencies.includes(sector.name);
                          const isDimmed = isHoveringWithDeps && !isHoveredBlock && !isDependencyBlock;
                          const isTargetSector = viewLens === 'valuation' && isHighAlphaTarget(sector);

                          return (
                            <div 
                              key={sIdx}
                              onMouseEnter={() => setHoveredSector(`${layer.id}-${sIdx}`)}
                              onMouseLeave={() => setHoveredSector(null)}
                              onClick={() => sector.hasDeepDive && setZoomedSectorData(sector.deepDive)}
                              className={`w-28 h-28 md:w-32 md:h-32 shrink-0 rounded-2xl border-2 transition-all duration-500 flex flex-col justify-start p-2.5 relative group/sector
                                ${sector.active 
                                  ? `bg-slate-900/40 border-slate-700/80 text-white shadow-lg` 
                                  : 'bg-slate-900/20 border-slate-800/60 text-slate-300'
                                }
                                ${(viewLens === 'risk' && sector.wrapper && !isDimmed) ? 'border-rose-500/50 ring-1 ring-rose-500/20 bg-rose-500/5' : ''}
                                ${(isTargetSector && !isDimmed) ? `border-${layer.color}-500/50 ring-1 ring-${layer.color}-500/20 bg-slate-800/60 shadow-lg shadow-${layer.color}-500/20` : ''}
                                ${isHoveredBlock && !sector.wrapper ? `border-${layer.color}-500/80 bg-slate-800 scale-105 z-20 shadow-[0_0_30px_rgba(var(--${layer.color}-500-rgb),0.3)]` : ''}
                                ${isDependencyBlock ? `border-emerald-500/80 bg-emerald-500/10 scale-[1.02] z-10 shadow-[0_0_20px_rgba(16,185,129,0.3)]` : ''}
                                ${isDimmed ? 'opacity-20 grayscale blur-[1px]' : 'opacity-100'}
                                ${sector.hasDeepDive ? 'cursor-pointer' : 'cursor-default'}
                              `}
                            >
                               {isDependencyBlock && (
                                  <div className="absolute -top-2 -right-2 bg-emerald-500 text-slate-950 text-[7px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shadow-lg z-30 animate-pulse">
                                     Substrate
                                  </div>
                               )}

                               <div className="flex justify-between items-start w-full z-10 mb-3 gap-1">
                                  <div className={`flex items-start gap-1.5 opacity-90 transition-colors duration-300 ${isDependencyBlock ? 'text-white' : sector.active ? `text-${layer.color}-400` : 'text-slate-500 group-hover/sector:text-slate-400'}`}>
                                     <sector.icon size={12} className="shrink-0 mt-[1px]" />
                                     <span className="text-[7.5px] md:text-[8px] font-black uppercase tracking-widest leading-tight text-left">
                                        {sector.name}
                                     </span>
                                  </div>
                                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                                     {viewLens === 'valuation' && (
                                        <span className={`text-[7px] font-black px-1 py-0.5 rounded border leading-none ${sector.sovereignty === 'S' ? `text-emerald-400 border-emerald-500/40 bg-emerald-500/20` : 'text-rose-400 border-rose-500/40 bg-rose-500/20'}`}>
                                           {sector.sovereignty}
                                        </span>
                                     )}
                                     {viewLens === 'valuation' && (
                                        <span className="text-[6px] font-black text-slate-300 bg-slate-950/80 px-1 py-0.5 rounded tracking-tighter border border-slate-800 leading-none whitespace-nowrap">
                                          {sector.pivot}
                                        </span>
                                     )}
                                     {viewLens === 'risk' && sector.wrapper && (
                                        <div className="bg-rose-500/30 p-1 rounded border border-rose-500/40 animate-pulse">
                                           <AlertTriangle size={8} className="text-white" />
                                        </div>
                                     )}
                                  </div>
                               </div>

                               <div className="flex-1 w-full flex flex-col gap-1.5 z-10 overflow-hidden">
                                  {sector.firms && sector.firms.length > 0 ? (
                                     sector.firms.map(f => {
                                        const firmData = PortfolioData.find(pd => pd.company === f.name);
                                        return (
                                           <div key={f.name} className={`px-2 py-1.5 rounded-lg border border-${f.color}-500/30 bg-${f.color}-500/10 text-[7.5px] md:text-[8.5px] font-bold text-slate-200 w-full flex items-center justify-start gap-2 shadow-sm relative overflow-hidden group-hover/sector:border-${f.color}-500/60 transition-colors`}>
                                              <FirmLogo domain={firmData?.domain} company={f.name} className="w-4 h-4 rounded-[3px]" />
                                              <span className="truncate leading-tight">{f.name}</span>
                                           </div>
                                        );
                                     })
                                  ) : (
                                     <div className={`flex-1 w-full flex items-center justify-center transition-all duration-300 ${isTargetSector ? 'opacity-100' : 'opacity-30'}`}>
                                        {!sector.active && !isDependencyBlock && (
                                           isTargetSector 
                                            ? <Target size={24} className={`text-${layer.color}-400 animate-pulse drop-shadow-[0_0_8px_currentColor]`} />
                                            : <Lock size={16} className="text-slate-500" />
                                        )}
                                     </div>
                                  )}
                               </div>

                               {viewLens === 'risk' && (
                                 <div className="absolute bottom-1.5 inset-x-3 h-1 rounded-full overflow-hidden bg-slate-800/80 border border-slate-700/30">
                                    <div className={`h-full transition-all duration-700 ${sector.debt === 'Extreme' ? 'w-full bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.6)]' : sector.debt === 'High' ? 'w-3/4 bg-orange-500' : 'w-1/4 bg-emerald-500'}`}></div>
                                 </div>
                               )}

                               {sector.hasDeepDive && isHoveredBlock && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-slate-950/70 rounded-2xl z-20 backdrop-blur-[2px]">
                                     <Maximize2 size={16} className={`text-${layer.color}-400 animate-in zoom-in duration-300`} />
                                  </div>
                               )}
                            </div>
                          );
                        })}

                        {!isExpanded && inactiveCount > 0 && (
                          <div 
                            onClick={() => toggleLayer(layer.id)} 
                            className={`w-28 h-28 md:w-32 md:h-32 shrink-0 rounded-2xl bg-slate-900 flex flex-col items-center justify-center transition-all cursor-pointer group relative z-10 ml-1 ${
                              isTargetLayer
                                ? `border border-${layer.color}-500/50 hover:bg-slate-800/80 shadow-lg shadow-${layer.color}-500/20`
                                : 'border border-slate-700 hover:bg-slate-800 hover:border-slate-600 shadow-lg'
                            }`}
                          >
                             {isTargetLayer && (
                                <div className={`absolute inset-0 rounded-2xl border border-${layer.color}-500/30 animate-pulse pointer-events-none`}></div>
                             )}
                             <div className="absolute inset-0 bg-slate-800 border border-slate-700 rounded-2xl -z-10 translate-x-1.5 translate-y-1.5 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-300 shadow-md"></div>
                             <div className="absolute inset-0 bg-slate-800/80 border border-slate-700/80 rounded-2xl -z-20 translate-x-3 translate-y-3 group-hover:translate-x-4 group-hover:translate-y-4 transition-transform duration-300 shadow-md"></div>
                             
                             {viewLens === 'valuation' ? (
                               <Target size={targetCount > 0 ? 24 : 20} className={targetCount > 0 ? `text-${layer.color}-400 mb-1 group-hover:text-${layer.color}-300 transition-colors animate-pulse drop-shadow-[0_0_8px_currentColor]` : `text-slate-600 mb-1 group-hover:text-slate-500 transition-colors`} />
                             ) : (
                               <Box size={20} className="text-slate-500 mb-1 group-hover:text-indigo-400 transition-colors" />
                             )}
                             
                             <div className={`text-lg font-black leading-none mb-1 ${viewLens === 'valuation' && targetCount === 0 ? 'text-slate-600' : 'text-white'}`}>
                               +{viewLens === 'valuation' ? targetCount : inactiveCount}
                             </div>
                             <div className={`text-[7px] font-black uppercase tracking-widest text-center leading-tight transition-colors ${
                                viewLens === 'valuation' 
                                  ? (targetCount > 0 ? `text-${layer.color}-500 group-hover:text-${layer.color}-400` : 'text-slate-600 group-hover:text-slate-500') 
                                  : 'text-slate-500 group-hover:text-slate-300'
                             }`}>
                               {viewLens === 'valuation' ? <>Strategic<br/>Targets</> : <>Uncharted<br/>Sectors</>}
                             </div>
                          </div>
                        )}

                        {isExpanded && inactiveCount > 0 && (
                          <div 
                             onClick={() => toggleLayer(layer.id)} 
                             className="w-28 h-28 md:w-32 md:h-32 shrink-0 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col items-center justify-center hover:bg-slate-800 hover:border-slate-700 transition-colors cursor-pointer group ml-1"
                           >
                              <MoveLeft size={20} className="text-slate-500 group-hover:text-indigo-400 transition-colors mb-2 group-hover:-translate-x-1 duration-300" />
                              <div className="text-[8px] font-black uppercase tracking-widest text-slate-500 group-hover:text-white transition-colors">Collapse</div>
                           </div>
                        )}
                     </div>
                  </div>
              </div>
            </div>
            );
          })}
        </div>

        <div className="mt-12 w-full max-w-4xl mx-auto">
          {viewLens === 'risk' && (
            <div className="p-6 md:p-8 rounded-3xl bg-rose-500/10 border border-rose-500/30 shadow-[0_0_40px_rgba(244,63,94,0.15)] animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col md:flex-row items-start md:items-center gap-6">
               <div className="p-4 bg-[#0a0f1e] border-2 border-rose-500/50 rounded-2xl shrink-0 shadow-lg">
                  <AlertTriangle size={32} className="text-rose-400 animate-pulse" />
               </div>
               <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.3em] text-rose-400 mb-2 flex items-center gap-2">
                     <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping"></span> Mandate: Agentic Pivot
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white tracking-tight mb-2">Logical Wrappers Face Imminent Disruption</h3>
                  <p className="text-sm text-rose-200/80 font-medium leading-relaxed">
                     Ideaspring is over-indexed in L3/L4 Wrappers. Audit these assets for 'Heuristic Half-Life'. <strong className="text-white">Force a pivot to Outcome-Based Yield</strong> or initiate a 'Heuristic Harvest' (data extraction) for strategic exit before multiple collapses.
                  </p>
               </div>
            </div>
          )}

          {viewLens === 'valuation' && (
            <div className="p-6 md:p-8 rounded-3xl bg-emerald-500/10 border border-emerald-500/30 shadow-[0_0_40px_rgba(16,185,129,0.15)] animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col md:flex-row items-start md:items-center gap-6">
               <div className="p-4 bg-[#0a0f1e] border-2 border-emerald-500/50 rounded-2xl shrink-0 shadow-lg">
                  <Target size={32} className="text-emerald-400 animate-pulse" />
               </div>
               <div className="flex-1">
                  <div className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-400 mb-2 flex items-center gap-2">
                     <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span> Mandate: Substrate Hedge
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white tracking-tight mb-2">Structural Fragility & Substrate Deficit</h3>
                  <p className="text-sm text-emerald-200/80 font-medium leading-relaxed mb-4">
                     Ideaspring is currently <strong className="text-white">Renting Alpha</strong> from substrates it does not control. Your L3 software multiples are capped by the fragility of your L1/L2 foundations. Acquire 'Origin Nodes' in high-velocity physical sectors to underwrite your L3 logic.
                  </p>
                  
                  <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-4">
                     <div className="text-[9px] font-black uppercase tracking-widest text-emerald-400 mb-3 flex items-center gap-1.5"><Search size={10}/> High-Alpha Underwriting Targets</div>
                     <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        <div className="flex items-start gap-2.5">
                           <span className="text-[9px] font-bold text-slate-300 bg-slate-900/80 border border-indigo-500/40 px-2.5 py-1 rounded shrink-0 w-44 flex justify-between items-center">Orbital PNT (L2) <span className="text-indigo-400/80 ml-1 text-[8px] font-black">30x</span></span>
                           <span className="text-[9px] text-slate-400 leading-snug pt-0.5">Secures the 'Perimeter' for all L2 mobility data.</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                           <span className="text-[9px] font-bold text-slate-300 bg-slate-900/80 border border-emerald-500/40 px-2.5 py-1 rounded shrink-0 w-44 flex justify-between items-center">Energy Origins (L1) <span className="text-emerald-500/80 ml-1 text-[8px] font-black">15x</span></span>
                           <span className="text-[9px] text-slate-400 leading-snug pt-0.5">The physical Artery for 'Compute' cost-stability.</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                           <span className="text-[9px] font-bold text-slate-300 bg-slate-900/80 border border-emerald-500/40 px-2.5 py-1 rounded shrink-0 w-44 flex justify-between items-center">Silicon Sovereignty (L1) <span className="text-emerald-500/80 ml-1 text-[8px] font-black">25x</span></span>
                           <span className="text-[9px] text-slate-400 leading-snug pt-0.5">Decouples 'Reasoning' from third-party hardware.</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                           <span className="text-[9px] font-bold text-slate-300 bg-slate-900/80 border border-cyan-500/40 px-2.5 py-1 rounded shrink-0 w-44 flex justify-between items-center">GovTech Spines (L3) <span className="text-cyan-500/80 ml-1 text-[8px] font-black">15x</span></span>
                           <span className="text-[9px] text-slate-400 leading-snug pt-0.5">Sovereign administrative logic & state security.</span>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
          )}
        </div>

        <div className="mt-10 text-center border-t border-slate-900/80 pt-6">
           <p className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-600 flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4">
             <span className="opacity-40">Systemic Map: Alpha Structurally Rolls Up From Origin Nodes</span>
             <span className="opacity-20 hidden md:block">•</span>
             <span className="opacity-40">© 2026 Frontier Intelligence Group</span>
           </p>
        </div>
      </div>

      {zoomedSectorData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-12">
          <div className="absolute inset-0 bg-[#020617]/95 backdrop-blur-2xl" onClick={() => setZoomedSectorData(null)}></div>
          <div className="relative w-full max-w-7xl max-h-full overflow-y-auto scrollbar-hide bg-[#060918] border border-slate-800 rounded-[48px] shadow-2xl flex flex-col animate-in fade-in zoom-in duration-300">
             <div className="relative z-10 p-10 lg:p-16 border-b border-slate-800 flex flex-col md:flex-row md:items-end justify-between gap-10">
                <div className="flex items-center gap-8">
                   <div className={`p-6 bg-[#0a0f1e] border-2 border-${zoomedSectorData.themeColor}-500/30 rounded-3xl shadow-2xl`}>
                      <Truck size={56} className={`text-${zoomedSectorData.themeColor}-400`} />
                   </div>
                   <div>
                      <div className={`text-[11px] font-black uppercase tracking-[0.3em] text-${zoomedSectorData.themeColor}-400 mb-4 flex items-center gap-3`}>
                         <div className={`w-2.5 h-2.5 rounded-full bg-${zoomedSectorData.themeColor}-400 animate-pulse shadow-[0_0_10px_rgba(var(--${zoomedSectorData.themeColor}-500-rgb),0.5)]`}></div>
                         Live Asset Intelligence Sync
                      </div>
                      <h2 className="text-5xl lg:text-7xl font-black text-white tracking-tighter uppercase leading-none">
                         {zoomedSectorData.fullName}
                      </h2>
                   </div>
                </div>
                <div className="flex items-center gap-5 bg-slate-900/50 border border-slate-800 rounded-3xl p-6 backdrop-blur-sm">
                   <img src={zoomedSectorData.custodian.img} alt={zoomedSectorData.custodian.name} className="w-16 h-16 rounded-full object-cover border-2 border-slate-700 shadow-xl" />
                   <div>
                      <div className={`text-[9px] font-black uppercase tracking-widest text-${zoomedSectorData.themeColor}-500 mb-1`}>Brain Custodian</div>
                      <div className="text-lg font-bold text-white leading-tight">{zoomedSectorData.custodian.name}</div>
                      <div className="text-[11px] text-slate-400 mt-1 uppercase font-bold tracking-widest">{zoomedSectorData.custodian.role}</div>
                   </div>
                </div>
             </div>
             <div className="p-10 lg:p-16 bg-[#050814]">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                   {zoomedSectorData.slices.map((slice, i) => (
                      <div key={i} className="bg-[#0a0f1e]/80 border border-slate-800/60 rounded-[40px] p-8 hover:border-slate-700 transition-all group relative overflow-hidden">
                         <div className="flex items-center justify-between mb-8">
                            <div className={`p-3.5 bg-slate-900 border border-slate-800 rounded-2xl text-${zoomedSectorData.themeColor}-500 group-hover:scale-110 transition-transform`}>
                               <slice.icon size={28} />
                            </div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 border border-slate-800/50 px-3 py-1 rounded-full">{slice.layer}</span>
                         </div>
                         <h4 className="text-2xl font-black text-white mb-4 tracking-tight leading-tight">{slice.name}</h4>
                         <p className="text-sm text-slate-400 leading-relaxed font-medium mb-8">{slice.desc}</p>
                         <div className="mt-auto flex items-start gap-3 pt-6 border-t border-slate-800/50">
                            <Globe size={14} className={`text-${zoomedSectorData.themeColor}-500 mt-1`} />
                            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 leading-relaxed">
                               FOCUS: <span className="text-slate-300">{slice.focus}</span>
                            </div>
                         </div>
                      </div>
                   ))}
                </div>
             </div>
             <button onClick={() => setZoomedSectorData(null)} className="absolute top-10 right-10 p-4 bg-slate-900 border border-slate-700 rounded-full text-slate-400 hover:text-white transition-all shadow-2xl z-50">
                <X size={24} />
             </button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- SMARTSHIP DEEP DIVE CONSTANTS & COMPONENTS ---
const SMARTSHIP_COLORS = {
  emerald: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  cyan: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/20' },
  blue: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/20' },
  indigo: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500/20' },
};

const SMARTSHIP_STACK_DATA = [
  { level: 'L4', title: 'Orchestration', subtitle: 'Compliance & Network Ecosystem', sshRole: 'The Compliance OS: Real-time ESG scoring & Port-to-Ship synchronization.', color: 'emerald', icon: Globe },
  { level: 'L3', title: 'The Brain', subtitle: 'Agentic Execution Layer', sshRole: 'Predictive Exception Management: Autonomously triggering RFQs & routing fixes.', color: 'cyan', icon: Cpu },
  { level: 'L2', title: 'Nervous System', subtitle: 'Data Middleware & Connectivity', sshRole: 'Maritime Middleware: Harmonizing 20+ legacy hardware OEMs into one stream.', color: 'blue', icon: Activity },
  { level: 'L1', title: 'Physical Asset', subtitle: 'The Software-Defined Vessel', sshRole: 'Zero-Retrofit Gateway: Grafting intelligence onto legacy brownfield hardware.', color: 'indigo', icon: Anchor }
];

const SMARTSHIP_OPPORTUNITIES = [
  { id: 1, title: 'The "Brownfield" Retrofit Gold Rush', alpha: '80% Global Fleet Reach', desc: 'Democratizing intelligence for the "Legacy Long-Tail." While competitors focus on expensive newbuilds, SSH captures the mid-market via plug-and-play logic injection.', impact: 'L1 ↔ L2 Shift', icon: Zap, metrics: '+15% Fleet Intelligence' },
  { id: 2, title: 'Compliance as a Service (CaaS)', alpha: 'ESG Alpha Gateway', desc: 'Turning carbon intensity (CII/EEXI) from a liability into a financial asset. SSH provides verified "Ground Truth" data required for Green Loans and port entry.', impact: 'L4 Regulatory Hub', icon: ShieldCheck, metrics: '100% Audit Readiness' },
  { id: 3, title: 'Agentic Loop Closure', alpha: 'Autonomous Execution', desc: 'Moving from alerts to actions. When the SSH brain detects a Main Engine anomaly, it autonomously checks spares in the next port and drafts the procurement RFQ.', impact: 'L3 Logic Injection', icon: Cpu, metrics: '-30% Downtime' },
  { id: 4, title: 'Visual AI Intelligence', alpha: 'The Fourth Sense', desc: 'Transforming deck cameras from passive recorders into active sensors. Real-time safety monitoring, Piracy detection, and Cargo integrity verification.', impact: 'L2 ↔ L3 Vision', icon: Eye, metrics: 'Total Situational Awareness' },
  { id: 5, title: 'Technical Debt Arbitrage', alpha: 'M&A Value Creation', desc: 'The Private Equity Play: Buy aging, technically bankrupt fleets at a discount, inject the SSH brain to modernize performance, and flip as sustainable assets.', impact: 'PE Strategy Lead', icon: TrendingUp, metrics: '10-12% Fuel ROI' }
];

const SmartShipCard = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-md overflow-hidden ${className}`}>
    {children}
  </div>
);

const SmartShipBadge = ({ text, color = "emerald" }) => (
  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${SMARTSHIP_COLORS[color].bg} ${SMARTSHIP_COLORS[color].text} border ${SMARTSHIP_COLORS[color].border}`}>
    {text}
  </span>
);

const SmartShipDeepDiveView = () => {
  const [activeLayer, setActiveLayer] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="relative w-full h-full text-slate-200 selection:bg-emerald-500/30">
      {/* Local Background Gradients scoped to this container */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-emerald-500/10 blur-[120px] rounded-full" />
        <div className="absolute top-[20%] -right-[10%] w-[30%] h-[30%] bg-cyan-500/10 blur-[100px] rounded-full" />
      </div>

      <div className={`relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        
        {/* HEADER SECTION */}
        <MandateHeader
          title="ASSET: SMARTSHIP"
          metric="SPINE NODE"
          pulseLabel="CONVICTION"
          pulseText="High. Primary alpha vector identified in ESG Data Layer."
          pulseColor="cyan"
        />

        {/* SECTION 1: THE STACK PLACEMENT */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-emerald-400" />
                Stack Placement: The Sector OS
              </h2>
              <p className="text-sm text-slate-400 leading-relaxed font-medium">
                Smart Ship Hub functions as a <strong>Layer 2 (Middleware)</strong> and <strong>Layer 3 (Executive)</strong> hybrid, serving as the high-frequency nervous system for the modern vessel.
              </p>
            </div>

            <div className="space-y-3">
              {SMARTSHIP_STACK_DATA.map((item, idx) => {
                const isCore = item.level === 'L2' || item.level === 'L3';
                return (
                <div 
                  key={idx}
                  onMouseEnter={() => setActiveLayer(idx)}
                  onMouseLeave={() => setActiveLayer(null)}
                  className={`group relative p-4 rounded-xl border transition-all duration-300 cursor-default
                    ${activeLayer === idx 
                      ? 'bg-slate-800/80 border-slate-600 translate-x-2 shadow-lg' 
                      : isCore 
                        ? 'bg-cyan-950/20 border-cyan-500/30 opacity-100 shadow-[0_0_15px_rgba(6,182,212,0.05)]' 
                        : 'bg-white/5 border-white/5 opacity-50 hover:opacity-80'}
                  `}
                >
                  {isCore && (
                    <div className="absolute -top-2.5 right-6 px-2 py-0.5 bg-slate-950 border border-cyan-500/40 rounded text-[7px] font-black uppercase tracking-widest text-cyan-400 flex items-center gap-1.5 shadow-lg z-10 transition-all duration-300 group-hover:border-cyan-400/80 group-hover:shadow-[0_0_10px_rgba(6,182,212,0.2)]">
                      <span className="w-1 h-1 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_5px_currentColor]"></span>
                      Core Domain
                    </div>
                  )}
                  {isCore && (
                    <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-md transition-all duration-300 ${activeLayer === idx ? 'h-8 bg-cyan-400 shadow-[0_0_8px_currentColor]' : 'h-4 bg-cyan-500/40'}`}></div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${SMARTSHIP_COLORS[item.color].bg} ${SMARTSHIP_COLORS[item.color].text} group-hover:scale-110 transition-transform`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-black text-white/30 tracking-widest uppercase">{item.level}</span>
                          <h3 className="font-bold text-white group-hover:text-emerald-400 transition-colors">{item.title}</h3>
                        </div>
                        <p className="text-[11px] text-slate-500 font-medium">{item.subtitle}</p>
                      </div>
                    </div>
                    {activeLayer === idx && <ArrowRight className="w-4 h-4 text-emerald-400 animate-pulse" />}
                  </div>
                  
                  {activeLayer === idx && (
                    <div className="mt-4 pt-4 border-t border-white/10 animate-in fade-in slide-in-from-top-2">
                      <p className="text-xs text-slate-300 italic font-medium">" {item.sshRole} "</p>
                    </div>
                  )}
                </div>
              )})}
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col justify-center">
            <SmartShipCard className="relative h-full min-h-[400px] flex items-center justify-center border-emerald-500/10 p-8 overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.05)_0%,transparent_70%)]" />
              
              <div className="relative z-10 w-full max-w-md space-y-8 animate-float">
                <div className="text-center space-y-2">
                  <SmartShipBadge text="Strategic Positioning" color="cyan" />
                  <h3 className="text-2xl font-black text-white">Digital Twin Continuity</h3>
                  <p className="text-sm text-slate-400 font-medium leading-relaxed">The "Lock-in" Effect: Once a vessel identity is mapped, the switching cost becomes operational, not just financial.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10 space-y-1">
                    <div className="text-[10px] uppercase font-bold text-emerald-400 tracking-tighter">Fuel Efficiency</div>
                    <div className="text-2xl font-black text-white tracking-tight">Δ 12%</div>
                    <div className="text-[9px] text-slate-500 uppercase font-black">Avg Reduction via AI</div>
                  </div>
                  <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/10 space-y-1">
                    <div className="text-[10px] uppercase font-bold text-cyan-400 tracking-tighter">Maintenance</div>
                    <div className="text-2xl font-black text-white tracking-tight">Δ 30%</div>
                    <div className="text-[9px] text-slate-500 uppercase font-black">Reduction in Downtime</div>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/10 space-y-1">
                    <div className="text-[10px] uppercase font-bold text-blue-400 tracking-tighter">Reporting</div>
                    <div className="text-2xl font-black text-white tracking-tight">100%</div>
                    <div className="text-[9px] text-slate-500 uppercase font-black">Auto-Audit Ready</div>
                  </div>
                  <div className="p-4 rounded-xl bg-indigo-500/5 border border-indigo-500/10 space-y-1">
                    <div className="text-[10px] uppercase font-bold text-indigo-400 tracking-tighter">Integration</div>
                    <div className="text-2xl font-black text-white tracking-tight">Zero</div>
                    <div className="text-[9px] text-slate-500 uppercase font-black">Retrofit Hardware Required</div>
                  </div>
                </div>
              </div>
            </SmartShipCard>
          </div>
        </div>

        {/* SECTION 2: THE 5 MEGA-THESES */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              The 5 High-Conviction Opportunities
            </h2>
            <div className="h-px flex-1 bg-white/10 mx-6 hidden md:block" />
            <SmartShipBadge text="Mandate 2026" color="emerald" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SMARTSHIP_OPPORTUNITIES.map((opt) => (
              <SmartShipCard key={opt.id} className="group hover:border-emerald-500/30 transition-all duration-300 p-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center text-emerald-400 group-hover:bg-emerald-500/20 group-hover:scale-110 transition-all shadow-inner border border-slate-700">
                      <opt.icon className="w-6 h-6" />
                    </div>
                    <SmartShipBadge text={opt.alpha} color="emerald" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">{opt.title}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed mb-4 font-medium">
                    {opt.desc}
                  </p>
                </div>
                
                <div className="space-y-4 pt-4 border-t border-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-white/30 uppercase font-black">Strategic Impact</span>
                    <span className="text-xs text-cyan-400 font-bold tracking-tight">{opt.impact}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-white/30 uppercase font-black">Target Outcome</span>
                    <span className="text-xs text-emerald-400 font-bold tracking-tight">{opt.metrics}</span>
                  </div>
                </div>
              </SmartShipCard>
            ))}
            
            {/* The "Mic Drop" Verdict Box */}
            <SmartShipCard className="bg-emerald-500/10 border-emerald-500/30 p-8 flex flex-col items-center justify-center text-center space-y-6 lg:col-span-1">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center shadow-lg border border-emerald-500/30">
                <Scale className="w-8 h-8 text-emerald-400" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-white tracking-tighter">The "Mic Drop" Verdict</h3>
                <p className="text-xs text-emerald-200/80 leading-relaxed font-medium">
                  In 2026, the winners are those who own the <strong>Semantic Data Layer</strong> of the asset. Smart Ship Hub is currently the most agile contender to become the global standard for maritime middleware.
                </p>
              </div>
              <button className="w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase tracking-[0.2em] rounded-lg transition-all transform active:scale-95 shadow-[0_0_20px_rgba(16,185,129,0.3)]">
                Execute Mandate
              </button>
            </SmartShipCard>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-white/10 text-white/30 text-[10px] font-bold uppercase tracking-widest gap-4 mt-8 pb-4">
          <div className="flex items-center gap-6">
            <span>Proprietary Institutional Intelligence</span>
            <div className="w-1 h-1 rounded-full bg-white/20" />
            <span>SSH Audit v4.2</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-500/40" />
              <span>Verified Grounds</span>
            </div>
            <div className="h-4 w-px bg-white/10" />
            <span>© 2026 Strategy & Agents</span>
          </div>
        </div>

      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}} />
    </div>
  );
};

// --- HELPER FUNCTIONS RESTORED ---
const renderThesisWithHighlights = (thesis, color) => {
  const parts = thesis.split(/(\[[^\]]+\])/g);
  const styles = FUSION_COLORS[color];
  
  return parts.map((part, index) => {
    if (part.startsWith('[') && part.endsWith(']')) {
      const companyName = part.slice(1, -1);
      return (
        <span key={index} className={`inline-flex items-center justify-center px-1.5 py-px rounded-md border text-[9px] font-black uppercase tracking-widest mx-1 shadow-sm ${styles.text} ${styles.scoreBg}`}>
          {companyName}
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

const FloatingSynthesisCard = ({ fusionObj, setActiveFusionId }) => {
  const [cardPos, setCardPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  // Reset position if a new synergy is selected
  useEffect(() => {
    setCardPos({ x: 0, y: 0 });
  }, [fusionObj.id]);

  const handlePointerDown = (e) => {
    if (e.target.closest('button')) return; // Allow button clicks
    setIsDragging(true);
    dragStart.current = { x: e.clientX - cardPos.x, y: e.clientY - cardPos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    setCardPos({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
  };

  const handlePointerUp = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <div 
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{ transform: `translate(${cardPos.x}px, ${cardPos.y}px)`, touchAction: 'none' }}
      className={`fixed bottom-6 right-6 lg:bottom-10 lg:right-10 z-[110] w-[340px] md:w-[420px] p-6 pt-8 rounded-[24px] border animate-in fade-in slide-in-from-bottom-8 zoom-in-95 duration-500 shadow-2xl backdrop-blur-2xl bg-[#0a0f1e]/95 ${FUSION_COLORS[fusionObj.color].border} ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
    >
      {/* Drag indicator pill */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-slate-700/50"></div>
      
      <button onClick={() => setActiveFusionId(null)} className="absolute top-5 right-5 text-slate-500 hover:text-white transition-colors p-1.5 bg-slate-800/50 hover:bg-slate-700/50 rounded-full">
        <X size={14} />
      </button>
      
      <div className={`text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border w-fit mb-5 flex items-center gap-1.5 ${FUSION_COLORS[fusionObj.color].text} ${FUSION_COLORS[fusionObj.color].border} bg-${fusionObj.color}-500/10`}>
         <Zap size={10} className="animate-pulse" /> Synergy Detected
      </div>
      
      <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight leading-none mb-2 pr-6">
        {fusionObj.name.replace(/\s\([^)]+\)$/, '')}
      </h3>
      <div className={`text-[10px] font-bold uppercase tracking-widest mb-5 ${FUSION_COLORS[fusionObj.color].text}`}>
         Underwriting Score: {fusionObj.score}
      </div>
      
      <p className="text-xs text-slate-300 font-medium leading-loose mb-6">
        {renderThesisWithHighlights(fusionObj.thesis, fusionObj.color)}
      </p>
      
      <div className="flex items-center justify-between border-t border-slate-800/80 pt-5">
         <div>
            <div className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">Individual Avg</div>
            <div className="text-xl font-bold text-slate-300">~6x</div>
         </div>
         <ArrowRight size={16} className="text-slate-600" />
         <div className="text-right">
            <div className={`text-[8px] font-black uppercase tracking-widest mb-1 ${FUSION_COLORS[fusionObj.color].text}`}>Platform Potential</div>
            <div className={`text-3xl font-black leading-none drop-shadow-md ${FUSION_COLORS[fusionObj.color].text}`}>{fusionObj.multiple}</div>
         </div>
      </div>
    </div>
  );
};

const getScoreLabel = (scoreStr) => {
  const num = parseFloat(scoreStr);
  if (num >= 9.0) return "Very High";
  if (num >= 8.0) return "High";
  if (num >= 7.0) return "Medium";
  return "Low";
};

export default function IdeaSpringPortfolio() {
  const [activeTab, setActiveTab] = useState('cover');
  const [activeFusionId, setActiveFusionId] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const stackConfig = [
    { title: "04. Autonomous Intent", icon: Target, color: "purple", layerName: "Outcome", description: "Terminal workflow sovereignty & strategic accretion.", metric: "Strategic Accretion" },
    { title: "03. Cognitive Foundations", icon: Cpu, color: "cyan", layerName: "Reasoning", description: "Proprietary heuristics & cognitive logic engines.", metric: "Decision Velocity" },
    { title: "02. Digital Infrastructure", icon: Network, color: "indigo", layerName: "Perimeter", description: "The routing and flow layer for data sovereignty.", metric: "Data Sovereignty" },
    { title: "01. Sovereign Resilience", icon: Shield, color: "emerald", layerName: "Artery", description: "Foundational physical assets and origin moats.", metric: "Asset Control" }
  ];

  // Bulletproof extraction of all fusions
  const allFusionsFlat = [
    ...FUSION_OPPORTUNITIES.vertical,
    ...FUSION_OPPORTUNITIES.horizontal,
    ...FUSION_OPPORTUNITIES.resilience,
    ...FUSION_OPPORTUNITIES.efficiency
  ];
  
  const fusionObj = activeFusionId ? allFusionsFlat.find(f => f.id === activeFusionId) : null;
  const currentCount = fusionObj ? fusionObj.firms.length : PortfolioData.length;

  return (
    <div className="h-screen w-full bg-[#020617] text-slate-300 font-sans flex flex-col md:flex-row overflow-hidden relative">
      <NeuralMesh opacity={0.08} color="99, 102, 241" />
      
      {/* Sidebar Navigation - Hidden on Cover */}
      {activeTab !== 'cover' && (
        <nav className={`relative z-50 w-full shrink-0 border-b md:border-b-0 md:border-r border-slate-800 bg-[#020617]/90 backdrop-blur-xl flex flex-col transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'md:w-[88px]' : 'md:w-[280px]'}`}>
          
          {/* Collapse Toggle */}
          <button 
             onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
             className="absolute -right-3 top-8 bg-slate-900 border border-slate-700 rounded-full p-1 text-slate-400 hover:text-white z-50 hidden md:flex items-center justify-center shadow-lg hover:bg-slate-800 transition-all hover:scale-110"
          >
             {isSidebarCollapsed ? <ChevronRight size={14} strokeWidth={2.5} /> : <ChevronLeft size={14} strokeWidth={2.5} />}
          </button>

          <div className={`p-4 flex flex-col h-full transition-all duration-300 ${isSidebarCollapsed ? 'md:px-4 md:py-6 gap-8' : 'md:p-6 gap-6 md:gap-8'}`}>
            
            {/* Branding (Ideaspring Top) */}
            <div 
              onClick={() => setActiveTab('cover')}
              className={`hidden md:flex flex-col cursor-pointer group ${isSidebarCollapsed ? 'items-center mt-2' : ''}`}
            >
              <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2 mb-1.5'}`}>
                <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.6)] shrink-0"></div>
                {!isSidebarCollapsed && <h1 className="text-xl md:text-2xl font-black text-white group-hover:text-slate-200 transition-colors tracking-tighter uppercase leading-none truncate">Ideaspring</h1>}
              </div>
              {!isSidebarCollapsed && <div className="text-[8px] md:text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em] leading-none ml-4 truncate">Strategic Opportunity Audit</div>}
            </div>

            {/* Tabs */}
            <div className={`flex flex-row md:flex-col overflow-x-auto md:overflow-visible pb-2 md:pb-0 custom-scrollbar mt-2 ${isSidebarCollapsed ? 'gap-4' : 'gap-1.5'}`}>
              {[
                { id: 'sectors', num: '01', label: 'Macro Thesis', icon: Globe },
                { id: 'stack', num: '02', label: 'Alpha Fusion', icon: Layers },
                { id: 'matrix', num: '03', label: 'Execution Roadmap', icon: Target },
                { id: 'smartship', num: '04', label: 'SmartShip', icon: Anchor }
              ].map(tab => (
                <button 
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  title={isSidebarCollapsed ? tab.label : undefined}
                  className={`group w-full shrink-0 flex items-center rounded-xl text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all duration-300 border ${
                    isSidebarCollapsed ? 'justify-center py-3 px-0' : 'justify-between py-2.5 md:py-3 px-3'
                  } ${
                    activeTab === tab.id 
                      ? 'bg-indigo-500/10 text-white border-indigo-500/50 shadow-[0_0_20px_rgba(99,102,241,0.15)]' 
                      : 'bg-transparent text-slate-500 border-transparent hover:text-slate-300 hover:bg-slate-800/50'
                  }`}
                >
                  <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2.5'}`}>
                    <tab.icon size={isSidebarCollapsed ? 18 : 14} className={activeTab === tab.id ? "text-indigo-400" : "text-slate-500 group-hover:text-slate-400"} /> 
                    {!isSidebarCollapsed && (
                      <span className="whitespace-nowrap flex items-center gap-1.5">
                        <span className={`opacity-50 text-[8px] ${activeTab === tab.id ? 'text-indigo-400' : ''}`}>{tab.num}.</span>
                        <span>{tab.label}</span>
                      </span>
                    )}
                  </div>
                  {activeTab === tab.id && !isSidebarCollapsed && (
                    <div className="hidden md:flex items-center justify-center ml-2 shrink-0">
                       <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse shadow-[0_0_10px_rgba(99,102,241,0.8)]"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Context-Locked Underwriting HUD */}
            <div className={`mt-auto pt-6 border-t border-slate-800 transition-all ${isSidebarCollapsed ? 'px-0 flex flex-col items-center gap-3' : ''}`}>
               {!isSidebarCollapsed && <div className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-3">Context-Locked Underwriting</div>}
               <div className={`flex flex-col ${isSidebarCollapsed ? 'gap-4 items-center' : 'gap-2.5'}`}>
                 <div className="flex items-center justify-between w-full" title="Sector Brains: Fully Ingested">
                    <div className={`flex items-center gap-2 text-[9px] font-bold text-slate-300 ${isSidebarCollapsed ? 'justify-center' : ''}`}>
                       <CheckCircle2 size={isSidebarCollapsed ? 14 : 12} className="text-emerald-500 shrink-0" /> {!isSidebarCollapsed && 'Sector Brains'}
                    </div>
                    {!isSidebarCollapsed && <span className="text-[7px] font-black uppercase text-emerald-500/80 bg-emerald-500/10 px-1.5 py-0.5 rounded">Ingested</span>}
                 </div>
                 <div className="flex items-center justify-between w-full" title="Ideaspring DNA: Partial / Public Proxy">
                    <div className={`flex items-center gap-2 text-[9px] font-bold text-slate-300 ${isSidebarCollapsed ? 'justify-center' : ''}`}>
                       <div className={`rounded-full border border-amber-500/50 flex items-center justify-center shrink-0 ${isSidebarCollapsed ? 'w-3.5 h-3.5' : 'w-3 h-3'}`}><div className={`${isSidebarCollapsed ? 'w-2 h-2' : 'w-1.5 h-1.5'} rounded-full bg-amber-500 animate-pulse`}></div></div>
                       {!isSidebarCollapsed && 'Ideaspring DNA'}
                    </div>
                    {!isSidebarCollapsed && <span className="text-[7px] font-black uppercase text-amber-500/80 bg-amber-500/10 px-1.5 py-0.5 rounded">Partial</span>}
                 </div>
                 <div className="flex items-center justify-between w-full" title="SmartShip Context: Partial / Metadata">
                    <div className={`flex items-center gap-2 text-[9px] font-bold text-slate-300 ${isSidebarCollapsed ? 'justify-center' : ''}`}>
                       <div className={`rounded-full border border-amber-500/50 flex items-center justify-center shrink-0 ${isSidebarCollapsed ? 'w-3.5 h-3.5' : 'w-3 h-3'}`}><div className={`${isSidebarCollapsed ? 'w-2 h-2' : 'w-1.5 h-1.5'} rounded-full bg-amber-500 animate-pulse`}></div></div>
                       {!isSidebarCollapsed && 'SmartShip Context'}
                    </div>
                    {!isSidebarCollapsed && <span className="text-[7px] font-black uppercase text-amber-500/80 bg-amber-500/10 px-1.5 py-0.5 rounded">Partial</span>}
                 </div>
               </div>
            </div>

            {/* Mandate Info Box (Frontier Bottom) */}
            {!isSidebarCollapsed && (
              <div className="mt-6 hidden md:block animate-in fade-in duration-300">
                <div className="p-4 bg-slate-900/40 border border-slate-800/80 rounded-2xl relative overflow-hidden flex items-center gap-3">
                   <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-500/5 rounded-full blur-[20px] pointer-events-none"></div>
                   <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center shadow-[0_0_20px_rgba(99,102,241,0.4)] shrink-0 z-10">
                      <Command size={16} className="text-white" />
                   </div>
                   <div className="relative z-10">
                     <div className="text-xs font-black text-white tracking-tight uppercase leading-none mb-1">Frontier</div>
                     <div className="text-[8px] text-indigo-400/80 uppercase font-bold tracking-widest leading-none">Agentic Alpha Foundry</div>
                   </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className={`flex-1 relative z-10 h-full overflow-y-auto custom-scrollbar ${activeTab === 'cover' ? 'p-0' : 'p-6 md:p-10 lg:p-12'}`}>
        <div className="max-w-7xl mx-auto w-full h-full">
          {activeTab === 'cover' ? (
            <CoverView onStartBriefing={() => setActiveTab('sectors')} />
          ) : activeTab === 'sectors' ? (
            <SectorBrainsVault />
          ) : activeTab === 'stack' ? (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
              
              <MandateHeader
                title="CROSS-PORTFOLIO ALPHA FUSION"
                metric="4 ALPHA VECTORS"
                pulseLabel="STRATEGY"
                pulseText="Synthesize L1+L2 nodes to manufacture sovereign platforms."
                pulseColor="emerald"
              />

              {/* Sovereign Quad-Layout (The Alpha Thesis Engine) */}
              <div className="flex flex-col gap-6 mb-12 relative z-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 w-full">
                  {LENS_CATEGORIES.map(category => (
                    <div key={category.id} className={`relative overflow-hidden bg-[#0a0f1e]/80 border border-${category.color}-500/30 rounded-3xl p-5 flex flex-col gap-4 group/quad shadow-xl backdrop-blur-md`}>
                      {/* Watermark Icon */}
                      <category.icon size={120} className={`absolute -bottom-10 -right-10 text-${category.color}-500/5 rotate-12 pointer-events-none transition-transform duration-700 group-hover/quad:scale-110 group-hover/quad:-rotate-12`} />

                      {/* Header */}
                      <div className="relative z-10 mb-2">
                        <div className={`text-[11px] font-black uppercase tracking-widest text-${category.color}-400 mb-2 flex items-center gap-2`}>
                          <category.icon size={14} /> {category.name}
                        </div>
                        <p className="text-[10px] text-slate-400 font-medium leading-relaxed">
                          {category.description}
                        </p>
                      </div>

                      {/* Tactical Modules (Opportunity Pills) */}
                      <div className="flex flex-col gap-2.5 relative z-10 mt-auto">
                         {FUSION_OPPORTUNITIES[category.id].map(fusion => {
                            const isActive = activeFusionId === fusion.id;
                            const numScore = parseFloat(fusion.score);
                            const bars = numScore >= 9.0 ? 3 : 2;

                            return (
                              <button
                                key={fusion.id}
                                onClick={() => setActiveFusionId(isActive ? null : fusion.id)}
                                className={`relative w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 backdrop-blur-md overflow-hidden ${
                                  isActive
                                    ? `bg-${fusion.color}-500/20 text-white border-2 border-${fusion.color}-400 shadow-lg shadow-${fusion.color}-500/40 scale-[1.02]`
                                    : `bg-slate-900/60 text-slate-400 border border-slate-700/80 hover:bg-slate-800 hover:border-${fusion.color}-500/50 hover:text-slate-200 shadow-md`
                                }`}
                              >
                                {/* Ignite Glow */}
                                {isActive && <div className={`absolute inset-0 bg-${fusion.color}-500/20 blur-md pointer-events-none`}></div>}
                                {isActive && <div className={`absolute left-0 top-0 bottom-0 w-1 bg-${fusion.color}-400 shadow-[0_0_10px_currentColor] pointer-events-none`}></div>}

                                <div className="flex items-center gap-2.5 relative z-10">
                                   <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? `bg-${fusion.color}-400 animate-pulse shadow-[0_0_10px_currentColor]` : `bg-${fusion.color}-500/40`}`}></span>
                                   <span className="font-bold">{fusion.shortName}</span>
                                </div>

                                {/* Signal Bars (Score Indicator) */}
                                <div className="flex items-end gap-0.5 h-3.5 relative z-10">
                                   <div className={`w-1 rounded-sm transition-colors duration-300 ${isActive ? `bg-${fusion.color}-300` : 'bg-slate-600'} h-1.5`}></div>
                                   <div className={`w-1 rounded-sm transition-colors duration-300 ${isActive ? `bg-${fusion.color}-300` : 'bg-slate-600'} h-2.5`}></div>
                                   <div className={`w-1 rounded-sm transition-colors duration-300 ${bars === 3 ? (isActive ? `bg-${fusion.color}-300` : 'bg-slate-600') : 'bg-slate-800/50'} h-3.5`}></div>
                                </div>
                              </button>
                            )
                         })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* The Interlock Narrative / Status */}
                <div className="flex justify-between items-center bg-slate-900/40 border border-slate-800/80 rounded-2xl p-4 shadow-lg backdrop-blur-md mt-2">
                   <div className="inline-flex items-center justify-center gap-2.5 px-5 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-[10px] font-black uppercase tracking-widest text-slate-400 shadow-inner">
                      <Activity size={14} className={fusionObj ? FUSION_COLORS[fusionObj.color].text : 'text-indigo-500'} />
                      <span>
                        {fusionObj ? 'SOVEREIGN MESH:' : 'SYSTEM STATUS:'} <strong className="text-white ml-1.5 text-xs">{currentCount}</strong> <span className="text-slate-500 ml-1">{fusionObj ? 'NODES SYNTHESIZED' : 'ACTIVE MODULES'}</span>
                      </span>
                   </div>
                   
                   {fusionObj && (
                      <button onClick={() => setActiveFusionId(null)} className="flex items-center gap-2 text-[9px] font-black uppercase text-slate-500 hover:text-white transition-colors">
                         <X size={12} /> Disengage Protocol
                      </button>
                   )}
                </div>
              </div>

              <div className="space-y-4">
                {stackConfig.map((layer, i) => (
                  <StackLayer 
                    key={i} 
                    {...layer} 
                    activeFusion={!!fusionObj} 
                    fusionData={fusionObj} 
                    isLast={i === stackConfig.length - 1}
                  />
                ))}
              </div>

              <div className="mt-10 text-center border-t border-slate-900/80 pt-6">
                 <p className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-600 flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4">
                   <span className="opacity-40">Architectural Synthesis: Cross-Portfolio Composability</span>
                   <span className="opacity-20 hidden md:block">•</span>
                   <span className="opacity-40">© 2026 Frontier Intelligence Group</span>
                 </p>
              </div>

              {/* Floating Synthesis Card */}
              {fusionObj && <FloatingSynthesisCard fusionObj={fusionObj} setActiveFusionId={setActiveFusionId} />}
            </div>
          ) : activeTab === 'matrix' ? (
            <MatrixView />
          ) : (
            <SmartShipDeepDiveView />
          )}
        </div>
      </main>
    </div>
  );
}
